import { derivedFrom, recordEditorLineage, buildLineageReport, downloadLineage } from './entityLineage'
import { isolatePopupEntities } from './isolatePopupEntities'
/* eslint-disable */
import { useState, useCallback, useRef, useMemo, useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import DxfCanvas from './DxfCanvas'
import Sidebar from './Sidebar'
import { selectionInfo } from './selectionInfo'
import RecentSessionsPanel from './features/recent-sessions/RecentSessionsPanel'
import SessionStats from './features/session-stats/SessionStats'
import { PerformanceMonitor } from './features/performance-monitor'
import { markPerf } from './shared/lib/perf-trace'
import {
  CLASS_COLORS, getLayers, buildClassifiedGroups,
  QBIQ_LAYER_NAMES, QLINE_ORDER, DRAW_LAYER_NAMES, moveEntity, rotateEntity, mirrorEntity, processColumnEntities, toDisplayName,
  getOriginalLayerStats, findSimilarEntities, findExactMatches, computeBounds, generateDoorQLines,
  applyProtectedLayers, stripProtection, reclassifiedLayerName, splitPolylineAtVertex, applyLineVertexEdit, insertVertexAtSegment, joinPolylines, explodePolyline,
} from './utils'
import { downloadDxf } from './dxfExport'
import { CollapsiblePanel } from './shared/ui'
import { useDropdown } from './shared/lib/hooks'
import { tessellatePolyline } from './bulge.js'
import { computeNetArea } from './floodFill.js'
import { appendDrawnEntity, floorEntities, floorNote, isFloorEntity, floorInput, floorResultIsCurrent, floorDrawingSnapshot } from './floorDetect'
import { SYMBOLS, instantiateSymbol } from './symbolLibrary.js'
import { prepareUploadDataUnits, scaleEntityCoords, editorEntity, planSizeM, cmFromUnit, UNIT_OPTIONS } from './uploadUnits.js'
import { getCanvasShortcutAction } from './keyboardShortcuts.js'
import { getManifestFitKey } from './canvasFitKey.js'
import {
  pollJobStatus, uploadDxf, classify as classifyRequest, detectFloor as detectFloorRequest, resolveFloor, previewFloorEdit, downloadAudit, saveFeedback, rerunLayerClassifier,
  agentClassify, agentChat, agentFeedback, agentStop, getMe, AGENT_POLL_TIMEOUT_MS,
  reopenSession, saveCanvasState,
} from './shared/api'
import {
  QbiqLogo, PlusIcon,
  IconClose, IconCheck, IconSplit, IconChatBubble, IconSend, IconFile,
  IconMove, IconCopy, IconJoin, IconExplode, IconRotate, IconSearch, IconLayers,
  IconCaretDown, IconChevronRight, IconTrash, IconCursor, IconRuler, IconPencil,
  IconShapes, IconRectangle, IconCircle, IconArc, IconPaintBucket, IconTolerance,
  IconDoorQLines, IconGrid, IconSymbolIT, IconSymbolEntrance, IconSymbolKitchen,
  IconSnap, IconUndo, IconRedo, IconZoomExtents, IconMinus, IconWarningTriangle,
  IconAlertCircle,
} from './shared/assets/icons'
import './App.css'
import AgentChat from './AgentChat'
import DrawingUnitsDialog from './DrawingUnitsDialog'
import { createCanvasSaver } from './canvasSave'
import { answerOps, applyAgentResult, freeChangeHandles, undoBatch } from './agentApply'

const DXF_UNITS_TO_CM = { 1: 2.54, 2: 30.48, 4: 0.1, 5: 1.0, 6: 100.0 }
const DXF_UNIT_NAMES  = { 1: 'inch', 2: 'feet', 4: 'mm', 5: 'cm', 6: 'm' }

async function readDxfUnits(file) {
  try {
    const text = await file.slice(0, 32768).text()
    const idx  = text.indexOf('$INSUNITS')
    if (idx === -1) return null
    const snippet = text.slice(idx, idx + 120)
    const m = snippet.match(/70\s*[\r\n]+\s*(\d+)/)
    if (!m) return null
    const code = parseInt(m[1], 10)
    return DXF_UNITS_TO_CM[code] != null
      ? { factor: DXF_UNITS_TO_CM[code], name: DXF_UNIT_NAMES[code] }
      : null
  } catch { return null }
}


// Convert raw DXF-unit length -> display string in meters
// unitScale: cm per raw DXF unit (from appliedScaleRef, 1 = unknown/unscaled)
function fmtLength(rawLen, unitScale) {
  const m = rawLen * (unitScale || 1) / 100
  return m >= 1000 ? `${(m / 1000).toFixed(1)}km` : `${m.toFixed(1)}m`
}

// All assignable class names (model output classes only, no Q-lines)
const ASSIGNABLE_CLASSES = [
  'Walls', 'Columns', 'Doors', 'Windows', 'Stairs', 'Railings',
  'Elevators', 'Mullions', 'Toilet seat', 'Interior Design', 'Furniture', '0',
]

function ProbPopup({ sublayer, groupColor, onClose, onRemap, splitData, sublayerHandles, onGroupSelect, onApplySplit, unitScale, existingOverrides }) {
  const [pos,            setPos]            = useState(null)
  const [tab,            setTab]            = useState('remap')   // 'remap' | 'split'
  const [selectedCls,    setSelectedCls]    = useState(null)      // remap tab selection
  const [splitMode,      setSplitMode]      = useState('count')   // 'count' | 'length'
  const [checkedClasses, setCheckedClasses] = useState(new Set())
  const [classOverrides, setClassOverrides] = useState({})
  const [activeRow,      setActiveRow]      = useState(null)
  const dragRef = useRef(null)

  const hasSplit  = splitData && (splitData.candidate_classes?.length ?? 0) > 0
  const splitGrps = hasSplit ? splitData.groups : null

  const onHeaderMouseDown = (e) => {
    if (e.target.closest('button,select')) return
    e.preventDefault()
    const rect = e.currentTarget.closest('.prob-popup').getBoundingClientRect()
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: rect.left, origY: rect.top }
    const onMove = (mv) => {
      if (!dragRef.current) return
      setPos({
        x: dragRef.current.origX + (mv.clientX - dragRef.current.startX),
        y: dragRef.current.origY + (mv.clientY - dragRef.current.startY),
      })
    }
    const onUp = () => { dragRef.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp) }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  const popupStyle = pos ? { left: pos.x, top: pos.y, bottom: 'auto', right: 'auto' } : {}

  const remapRows = (() => {
    const fusedProbs  = splitData?.fused_probs  || {}
    const layerProbs  = splitData?.layer_probs  || {}
    const entityProbs = splitData?.entity_probs || {}
    return ASSIGNABLE_CLASSES.map(cls => ({
      cls,
      fusedPct:  Math.round((fusedProbs[cls]  || 0) * 100),
      layerPct:  Math.round((layerProbs[cls]  || 0) * 100),
      entityPct: Math.round((entityProbs[cls] || 0) * 100),
    })).sort((a, b) => b.fusedPct - a.fusedPct)
  })()

  const remapMax = Math.max(...remapRows.map(r => r.fusedPct), 1)

  // For each class, compute remaining handles (those not yet overridden to another class)
  const remainingByClass = hasSplit
    ? Object.fromEntries(splitData.candidate_classes.map(c => {
        const handles = (splitGrps[c]?.handles || []).filter(h => sublayerHandles.includes(h) && !existingOverrides?.[h])
        return [c, handles]
      }))
    : {}

  const splitMax = hasSplit
    ? Math.max(...splitData.candidate_classes.map(c => {
        const g = splitGrps[c] || {}
        if (splitMode === 'count') return remainingByClass[c]?.length ?? 0
        // For length, scale proportionally to remaining count
        const origCount = g.count || 1
        const remCount  = remainingByClass[c]?.length ?? 0
        return remCount / origCount * (g.total_length || 0)
      }), 1)
    : 1

  const handleRowClick = (cls) => {
    const isActive = activeRow === cls
    // Toggle highlight
    setActiveRow(isActive ? null : cls)
    // Toggle checked-for-apply at the same time
    setCheckedClasses(prev => {
      const n = new Set(prev)
      isActive ? n.delete(cls) : n.add(cls)
      return n
    })
    if (isActive) {
      onGroupSelect(null, null)
    } else {
      const color   = CLASS_COLORS[cls] || '#6b7280'
      const handles = remainingByClass[cls] || []
      onGroupSelect(handles, new Map(handles.map(h => [h, color])))
    }
  }

  const toggleCheck = (cls, e) => {
    e.stopPropagation()
    // Sync with row active state
    const nowChecked = !checkedClasses.has(cls)
    setCheckedClasses(prev => { const n = new Set(prev); nowChecked ? n.add(cls) : n.delete(cls); return n })
    if (nowChecked) {
      setActiveRow(cls)
      const color   = CLASS_COLORS[cls] || '#6b7280'
      const handles = remainingByClass[cls] || []
      onGroupSelect(handles, new Map(handles.map(h => [h, color])))
    } else if (activeRow === cls) {
      setActiveRow(null); onGroupSelect(null, null)
    }
  }

  const applyGroups = () => {
    const assignments = {}
    for (const cls of checkedClasses) {
      const target  = classOverrides[cls] || cls
      const handles = remainingByClass[cls] || []
      if (!assignments[target]) assignments[target] = []
      assignments[target].push(...handles)
    }
    onApplySplit(sublayer.name, assignments)
  }

  return (
    <div className="prob-popup" style={popupStyle}>

      <div className="prob-header prob-drag-handle" onMouseDown={onHeaderMouseDown}>
        <div className="prob-title-row">
          <span className="prob-dot" style={{ background: groupColor }} />
          <span className="prob-title">{sublayer.name}</span>
        </div>
        <button className="icon-btn" onClick={onClose}>
          <IconClose />
        </button>
      </div>

      <div className="popup-tabs">
        <button className={tab === 'remap' ? 'active' : ''} onClick={() => { setTab('remap'); onGroupSelect(null, null); setSelectedCls(null) }}>Remap layer</button>
        <button className={tab === 'split' ? 'active' : ''} onClick={() => { setTab('split'); onGroupSelect(null, null); setSelectedCls(null) }}>Split layer</button>
      </div>

      {/* xx REMAP TAB xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx */}
      {tab === 'remap' && (
        <div className="popup-tab-body">

          <>
              <div className="remap-rows">
                {remapRows.map(({ cls, fusedPct, layerPct, entityPct }) => {
                  const color      = CLASS_COLORS[cls] || '#6b7280'
                  const isSelected = selectedCls === cls
                  const tooltip    = [
                    layerPct  > 0 ? `Layer: ${layerPct}%`  : null,
                    entityPct > 0 ? `Entity: ${entityPct}%` : null,
                  ].filter(Boolean).join('  ·  ')
                  return (
                    <div
                      key={cls}
                      className={`remap-row ${isSelected ? 'selected' : ''}`}
                      title={tooltip || undefined}
                      onClick={() => {
                        const next = isSelected ? null : cls
                        setSelectedCls(next)
                        if (next) {
                          // Use split group handles if available, otherwise color all sublayer geoms
                          const groupHandles = Object.values(splitGrps || {}).flatMap(g => g.handles)
                          const allHandles = groupHandles.length > 0 ? groupHandles : (sublayerHandles || [])
                          onGroupSelect(
                            allHandles.length > 0 ? allHandles : null,
                            allHandles.length > 0 ? new Map(allHandles.map(h => [h, color])) : null,
                          )
                        } else {
                          onGroupSelect(null, null)
                        }
                      }}
                    >
                      <span className="remap-dot" style={{ background: color }} />
                      <span className="remap-cls">{toDisplayName(cls)}</span>
                      <div className="remap-bar-track" style={{ flex: 1 }}>
                        <div className="remap-bar-fill"
                          style={{ width: `${Math.round(fusedPct / remapMax * 100)}%`, background: color }} />
                      </div>
                      <span className="remap-bar-pct">{fusedPct > 0 ? `${fusedPct}%` : ''}</span>
                      {isSelected && (
                        <IconCheck stroke={color} style={{ flexShrink: 0 }} />
                      )}
                    </div>
                  )
                })}
              </div>

              <button
                className="prob-approve-btn"
                disabled={!selectedCls}
                onClick={() => selectedCls && onRemap(sublayer.name, selectedCls)}
              >
                <IconCheck />
                {selectedCls ? `Remap -> ${toDisplayName(selectedCls)}` : 'Select a class above'}
              </button>
          </>
        </div>
      )}

      {/* xx SPLIT TAB xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx */}
      {tab === 'split' && (
        <div className="popup-tab-body">
          {!hasSplit && (
            <span className="hint" style={{ padding: '8px 4px', display: 'block' }}>No entity predictions  —  only layer-level classification available</span>
          )}
          {hasSplit && (<>

          <div className="split-title">
            <IconSplit />
            Geom split
            <div className="split-toggle">
              <button className={splitMode === 'count'  ? 'active' : ''} onClick={() => setSplitMode('count')}>Geoms</button>
              <button className={splitMode === 'length' ? 'active' : ''} onClick={() => setSplitMode('length')}>Length</button>
            </div>
          </div>

          <div className="split-rows">
            {splitData.candidate_classes.map(cls => {
              const g           = splitGrps[cls] || { count: 0, total_length: 0, handles: [] }
              const remHandles  = remainingByClass[cls] || []
              const remCount    = remHandles.length
              const origCount   = g.count || 1
              const remLength   = remCount / origCount * (g.total_length || 0)
              const val         = splitMode === 'count' ? remCount : remLength
              const barPct      = splitMax > 0 ? Math.round(val / splitMax * 100) : 0
              const color       = CLASS_COLORS[cls] || '#6b7280'
              const isActive    = activeRow === cls
              const isChecked   = checkedClasses.has(cls)
              const layerPct    = Math.round((splitData.layer_probs[cls] || 0) * 100)
              const targetCls   = classOverrides[cls] || cls
              const alreadyDone = remCount === 0 && g.count > 0
              return (
                <div
                  key={cls}
                  className={`split-row ${isActive ? 'active-row' : ''} ${isChecked ? 'checked' : ''} ${alreadyDone ? 'split-done' : ''}`}
                  title={`Layer classifier: ${layerPct}% · ${remCount} remaining (${g.count} total)  —  click to highlight`}
                  onClick={() => handleRowClick(cls)}
                >
                  <input
                    type="checkbox"
                    className="split-checkbox"
                    checked={isChecked}
                    onChange={e => toggleCheck(cls, e)}
                    onClick={e => e.stopPropagation()}
                  />
                  <span
                    className="split-cls-btn"
                    style={{ color }}
                  >
                    <span className="split-dot" style={{ background: color }} />
                    {cls}
                  </span>
                  <div className="split-bar-wrap">
                    <div className="split-bar-fill" style={{ width: `${barPct}%`, background: color }} />
                  </div>
                  <span className="split-count">
                    {splitMode === 'count' ? remCount : fmtLength(remLength, unitScale)}
                  </span>
                  <span className="split-layer-pct" style={{ color }}>{layerPct}%</span>
                  {isChecked && (
                    <select
                      className="split-class-select"
                      value={targetCls}
                      onChange={e => setClassOverrides(prev => ({ ...prev, [cls]: e.target.value }))}
                      onClick={e => e.stopPropagation()}
                    >
                      {ASSIGNABLE_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  )}
                </div>
              )
            })}
          </div>

          {checkedClasses.size > 0 && (
            <button className="split-accept-btn" onClick={applyGroups}>
              <IconCheck />
              Apply {checkedClasses.size} {checkedClasses.size > 1 ? 'groups' : 'group'}
            </button>
          )}
          </>)}
        </div>
      )}

    </div>
  )
}

function PreExportPopup({ data, classifyWarnings, onExport, onCancel }) {
  const { results, counts } = data

  return (
    <div className="pre-export-overlay">
      <div className="pre-export-popup">
        <div className="pre-export-header">
          <span className="pre-export-title">Review Before Export</span>
          <button className="icon-btn" onClick={onCancel}>
            <IconClose />
          </button>
        </div>

        {classifyWarnings?.length > 0 && (
          <div className="pre-export-warnings">
            {classifyWarnings.map((w, i) => (
              <div key={i} className="pre-export-warn-row">!  {w}</div>
            ))}
          </div>
        )}

        <div className="pre-export-rows">
          {results.map(r => {
            const color     = CLASS_COLORS[r.layer_name] || '#6b7280'
            const classProb = Math.round((r.probs?.[r.layer_name] ?? 0) * 100)
            const agrees    = r.agrees
            return (
              <div
                key={r.layer_name}
                className={`pre-export-row ${!agrees ? 'disagrees' : ''}`}
              >
                <span className="pre-export-dot" style={{ background: color }} />
                <span className="pre-export-cls">{r.layer_name}</span>
                <span className="pre-export-count">{counts[r.layer_name] ?? 0}</span>
                <div className="pre-export-bar-wrap">
                  <div
                    className="pre-export-bar-fill"
                    style={{ width: `${classProb}%`, background: agrees ? color : '#f59e0b' }}
                  />
                </div>
                <span className="pre-export-pct" style={{ color: agrees ? color : '#f59e0b' }}>
                  {classProb}%
                </span>
                {!agrees && (
                  <span className="pre-export-disagree" title={`Model suggests: ${r.suggested_class} (${r.confidence}%)`}>! </span>
                )}
              </div>
            )
          })}
        </div>

        <div className="pre-export-footer">
          <button className="btn btn-primary btn-sm" onClick={onExport}>
            Export
          </button>
        </div>
      </div>
    </div>
  )
}

function ConfRemapPanel({ onApply, onClose }) {
  const [high, setHigh] = useState(85)
  const [low,  setLow]  = useState(60)
  return (
    <div className="conf-remap-panel">
      <div className="conf-remap-header">
        <span>Auto-Remap by Confidence</span>
        <button className="icon-btn" onClick={onClose}>
          <IconClose />
        </button>
      </div>
      <div className="conf-remap-row">
        <span className="conf-badge-label high">High {'>='}</span>
        <input type="number" min="0" max="100" value={high} onChange={e => setHigh(+e.target.value)} className="conf-num-input" />
        <span className="conf-hint">% {'->'} best class</span>
      </div>
      <div className="conf-remap-row">
        <span className="conf-badge-label low">Low &lt;</span>
        <input type="number" min="0" max="100" value={low} onChange={e => setLow(+e.target.value)} className="conf-num-input" />
        <span className="conf-hint">% {'->'} 0</span>
      </div>
      <div className="conf-remap-row">
        <span className="conf-badge-label med">Medium</span>
        <span className="conf-hint" style={{ flex:1 }}>{low}-{high}% {'->'} keep as-is</span>
      </div>
      <button className="btn btn-primary btn-sm" style={{ width:'100%', marginTop:8 }}
        onClick={() => { onApply(high, low); onClose() }}>Apply</button>
    </div>
  )
}

function ScaleDialog({ p1, p2, isScaledToCm, onApply, onClose }) {
  const worldDist = (p1 && p2) ? Math.hypot(p2[0] - p1[0], p2[1] - p1[1]) : null
  const [cm, setCm] = useState('')
  const [unit, setUnit] = useState('Centimeters')

  const applyMeasured = () => {
    const d = parseFloat(cm)
    if (!d || d <= 0 || !worldDist) return
    onApply(cmFromUnit(d, unit) / worldDist)
    onClose()
  }

  return (
    <div className="scale-dialog-overlay" onClick={onClose}>
      <div className="scale-dialog" onClick={e => e.stopPropagation()}>
        <div className="scale-dialog-header">
          <span>Calibrate Scale</span>
          <button className="icon-btn" onClick={onClose}><IconClose /></button>
        </div>
        {worldDist != null ? (
          <>
            <p className="scale-dialog-hint">
              Measured span: <strong>{worldDist.toFixed(2)}</strong> {isScaledToCm ? 'cm' : 'file units'}
            </p>
            <div className="scale-dialog-row">
              <label htmlFor="scale-real-length">Real distance:</label>
              <input
                id="scale-real-length"
                className="scale-dialog-input"
                type="number" min="0.001" step="any"
                value={cm}
                onChange={e => setCm(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') applyMeasured() }}
                autoFocus
              />
              <select className="scale-dialog-select scale-dialog-unit" aria-label="Unit" value={unit}
                onChange={e => setUnit(e.target.value)}>
                {UNIT_OPTIONS.map(o => <option key={o.label} value={o.label}>{o.label}</option>)}
              </select>
            </div>
            <button className="btn btn-primary btn-sm"
              style={{ width: '100%', marginTop: 8 }}
              disabled={!cm || parseFloat(cm) <= 0}
              onClick={applyMeasured}>
              Apply
            </button>
          </>
        ) : (
          <p className="scale-dialog-hint">Click two points on the drawing to measure a known distance.</p>
        )}
      </div>
    </div>
  )
}

const CHAT_STEPS = [
  { id: 'upload',   label: 'Upload a DXF/DWG file' },
  { id: 'classify', label: 'Run AI classification' },
  { id: 'review',   label: 'Review qbiq layers' },
  { id: 'approve',  label: 'Approve all layers' },
  { id: 'export',   label: 'Finish & export' },
]

const CANNED = [
  { k: ['upload','file','dxf','dwg','open'],
    r: 'Click the "Upload File" button in the top-right corner, or drag & drop your .dxf / .dwg onto the canvas.',
    hl: 'upload' },
  { k: ['classify','ai','run','detect','start'],
    r: 'After uploading, click the "Classify" button to run the AI  —  it will group your DXF layers into qbiq categories.',
    hl: 'classify' },
  { k: ['layer','layers','qbiq','category'],
    r: 'Switch to the Classified tab in the sidebar to see all qbiq layers. Expand any layer to see which original DXF layers were mapped to it.',
    hl: 'tab-classified' },
  { k: ['approve','check','checkbox'],
    r: 'Check the checkbox next to each qbiq layer to approve it. Once all layers are approved the "Finish & Export" button unlocks.',
    hl: 'finish' },
  { k: ['export','download','save','finish'],
    r: 'When all layers are approved, click "Finish & Export" at the bottom of the sidebar to download the classified DXF.',
    hl: 'finish' },
  { k: ['focus','isolat','hide','show'],
    r: 'Click the eye icon on a layer row to focus it  —  other layers dim. Click the target icon to fully isolate it.',
    hl: null },
  { k: ['confidence','%','score','accuracy'],
    r: 'Confidence % shows how certain the AI was. Green >= 90%, yellow >= 75%, red < 75%. Use "Auto-Remap" to batch-reclassify low-confidence layers.',
    hl: null },
  { k: ['remap','reassign','wrong'],
    r: 'Open a sublayer in the Original tab to see class probabilities, then click any class chip to remap that layer.',
    hl: 'tab-original' },
  { k: ['draw','polyline','pen','add'],
    r: 'Select the Draw tool in the toolbar, choose a target layer from the dropdown, then click to add points. Press Enter to save, ESC to cancel.',
    hl: 'draw' },
  { k: ['undo','redo','ctrl','z'],
    r: 'Use Ctrl+Z to undo and Ctrl+Shift+Z to redo any move, erase, remap, or drawn polyline.',
    hl: 'undo' },
  { k: ['select','pick','erase','delete','arrow'],
    r: 'The Select tool (arrow) lets you click to select an entity. Drag on empty space for box-select. Press Backspace/Delete to erase.',
    hl: 'pan' },
  { k: ['scale','ruler','measure','cm','meter','distance'],
    r: 'Use the Scale tool in the toolbar: click two known points on the drawing, then enter the real-world distance in cm to calibrate.',
    hl: 'scale' },
  { k: ['zoom','fit','screen','extents','view'],
    r: 'Click the Zoom Extents button (expand arrows) to fit the whole drawing on screen.',
    hl: 'zoom-extents' },
  { k: ['original','preprocess','tab'],
    r: 'The Original tab shows raw DXF layers. Preprocessed shows cleaned layers. Classified shows the AI-grouped qbiq layers.',
    hl: 'tab-original' },
  { k: ['transfer','copy','preprocessed'],
    r: 'In the Original tab, select entities and click "Transfer to Preprocessed" in the selection bar.',
    hl: 'tab-original' },
]

function matchCanned(text) {
  const lower = text.toLowerCase()
  for (const entry of CANNED) {
    if (entry.k.some(word => lower.includes(word))) return entry
  }
  return { r: "I'm not sure about that. Try asking about: uploading, classifying, approving layers, exporting, the scale tool, drawing, or zoom.", hl: null }
}

function ChatPanel({ appState, onHighlight }) {
  const [open,     setOpen]     = useState(false)
  const [messages, setMessages] = useState([
    { from: 'agent', text: "Hi! I'm your qbiq guide. Ask me anything about the workflow, or check the steps below." }
  ])
  const [input, setInput] = useState('')
  const endRef = useRef(null)

  useEffect(() => { if (open) endRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages, open])

  const fire = (txt) => {
    const { r, hl } = matchCanned(txt)
    setMessages(prev => [...prev, { from: 'user', text: txt }, { from: 'agent', text: r }])
    if (hl) onHighlight?.(hl)
  }

  const send = () => {
    const txt = input.trim(); if (!txt) return
    fire(txt); setInput('')
  }

  const step =
    !appState.hasUpload   ? 'upload' :
    !appState.hasClassify ? 'classify' :
    !appState.allApproved ? (appState.approvedCount > 0 ? 'approve' : 'review') :
    'export'

  const currentIdx = CHAT_STEPS.findIndex(s => s.id === step)

  const QUICK = [
    'How do I upload?',
    'How does classify work?',
    'How do I approve layers?',
    'How do I export?',
  ]

  return (
    <>
      {/* Floating button */}
      <button className={`chat-fab ${open ? 'open' : ''}`} onClick={() => setOpen(o => !o)} title="Open guide">
        {open
          ? <IconClose width="16" height="16" />
          : <IconChatBubble />
        }
      </button>

      {open && (
        <div className="chat-panel">
          {/* Header */}
          <div className="chat-header">
            <div className="chat-header-left">
              <div className="chat-avatar"><IconChatBubble width="14" height="14" /></div>
              <span>qbiq classify agent</span>
            </div>
            <button className="icon-btn" onClick={() => setOpen(false)}>
              <IconClose />
            </button>
          </div>

          {/* Progress steps */}
          <div className="chat-steps">
            {CHAT_STEPS.map((s, i) => (
              <div key={s.id} className={`chat-step ${i < currentIdx ? 'done' : i === currentIdx ? 'active' : ''}`}>
                <span className="chat-step-dot">{i < currentIdx ? '✓' : i + 1}</span>
                <span className="chat-step-label">{s.label}</span>
              </div>
            ))}
          </div>

          {/* Messages */}
          <div className="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`chat-msg ${m.from}`}>
                <span>{m.text}</span>
              </div>
            ))}
            <div ref={endRef} />
          </div>

          {/* Quick replies */}
          <div className="chat-quick">
            {QUICK.map(q => (
              <button key={q} className="chat-quick-btn" onClick={() => fire(q)}>{q}</button>
            ))}
          </div>

          {/* Input */}
          <div className="chat-input-row">
            <input
              className="chat-input"
              placeholder="Ask me anything..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') send() }}
            />
            <button className="chat-send-btn" onClick={send} disabled={!input.trim()}>
              <IconSend />
            </button>
          </div>
        </div>
      )}
    </>
  )
}

const EMPTY_EDIT = {
  deletedHandles:      [],
  entityOverrides:     {},
  preprocessedEdits:   [],
  userPolylines:       [],   // entities drawn/placed by user in Classified view (always blue / isUserDrawn)
  preprocessedMods:    [],   // entities created by Explode/Join/Copy in Preprocess or Original view (classified by layer after Classify)
  layerRemaps:         {},
  handleClassOverrides: {},
  protectedLayers:     {},   // { handle: 'Interior Design' | 'Furniture' } — pinned in Preprocess, classifier output ignored
}

// Versioned canvas-state payload autosaved to the backend so a session can resume.
// Used by both the autosave effect and the reopen dedupe-priming so serializations match.
function buildCanvasPayload({ edit, classifyData, approvedLayers, activeView, visibleLayers, scaleFactor, appliedScale, unitsConfirmed }) {
  return {
    v: 1, appliedScale, scaleFactor, activeView, unitsConfirmed: !!unitsConfirmed,
    // Sorted so Set insertion order (e.g. toggling a layer off/on reorders it) doesn't change the
    // serialized dedupe key for an otherwise-unchanged set of layers.
    visibleLayers: [...visibleLayers].sort(), approvedLayers: [...approvedLayers].sort(),
    edit,
    classifyData: classifyData ? { manifest: classifyData.manifest, layer_split_data: classifyData.layer_split_data } : null,
  }
}

export default function App() {
  const { logout, user } = useAuth0()
  const [isStaff, setIsStaff] = useState(false)   // v1: classify-agent is for qbiq staff; the server says who
  useEffect(() => { getMe().then(me => setIsStaff(!!me.staff)) }, [user?.email])

  const [uploadData,   setUploadData]   = useState(null)
  const [classifyData, setClassifyData] = useState(null)
  const [sessionId,    setSessionId]    = useState(null)

  const [edit, setEdit] = useState(EMPTY_EDIT)
  const editRef         = useRef(edit)
  editRef.current       = edit
  const histRef         = useRef([])
  const futRef          = useRef([])

  const [activeView,      setActiveView]      = useState('original')
  const [visibleLayers,   setVisibleLayers]   = useState(new Set())
  const [activeTool,      setActiveTool]      = useState('pan')
  const [activeSymbol,    setActiveSymbol]    = useState(null)   // 'door' | 'toilet' | 'sink'
  const [selectedHandles, setSelectedHandles] = useState(new Set())
  const [pendingRemap,    setPendingRemap]    = useState(null)  // { origLayerName, newClass }
  const [focusedLayer,    setFocusedLayer]    = useState(null)  // isolated/focused layer key
  const [activeLayer,     setActiveLayer]     = useState('Walls')
  const [zoom,            setZoom]            = useState(1)
  const [scaleFactor,     setScaleFactor]     = useState(null)
  const [scaleDialog,     setScaleDialog]     = useState(null) // {p1, p2} or {}
  const [detectedUnits,   setDetectedUnits]   = useState(null)
  const [manuallyScaled,  setManuallyScaled]  = useState(false)
  const [unitsDialog,     setUnitsDialog]     = useState(false)  // Drawing units (AutoCAD UNITS) after upload
  const [unitsConfirmed,  setUnitsConfirmed]  = useState(false)  // user said OK -> classify sends their scale
  const unitsReturnRef = useRef(false)                           // reopen the units dialog after Scale by reference
  const [classifyAfterUnits, setClassifyAfterUnits] = useState(false)  // Classify opened the dialog: OK runs it
  const [soloLayers,      setSoloLayers]      = useState(new Set()) // isolated layer keys
  const [shiftSelectedLayers, setShiftSelectedLayers] = useState(new Set())
  const [snapEnabled,         setSnapEnabled]         = useState(true)
  const symbolDropdown   = useDropdown()
  const setLayerDropdown = useDropdown()
  const shapeDropdown    = useDropdown()
  const [activeShape,         setActiveShape]         = useState(null)  // 'rectangle'|'circle'|'arc'
  const [approvedLayers,  setApprovedLayers]  = useState(new Set())
  const [pendingMove,     setPendingMove]     = useState(null) // {mode:'move'|'copy', step:'base'|'dest', handles:Set, baseWX, baseWY}
  const [pendingTransform, setPendingTransform] = useState(null) // {mode:'rotate', step:'center'|'angle', handles, entities, cx?, cy?}
  const mirrorCycleRef = useRef({ step: 0, selKey: null, origOverrides: null })
  const [isFinishing,     setIsFinishing]     = useState(false)
  const [showWarningExport, setShowWarningExport] = useState(false)
  const [isRevalidating,   setIsRevalidating]   = useState(false)
  const [revalidateData,   setRevalidateData]   = useState(null)  // { results, counts }
  const [showPreExport,    setShowPreExport]    = useState(false)
  const [highlightEl,       setHighlightEl]       = useState(null)
  const hlTimerRef = useRef(null)
  const [isUploading,     setIsUploading]     = useState(false)
  const [isClassifying,   setIsClassifying]   = useState(false)
  const [isDetectingFloor, setIsDetectingFloor] = useState(false)
  // classify-agent: panel state machine (guide | running | preclean | review | result | none | failed)
  const [agent,        setAgent]        = useState({ view: 'idle' })  // active card: idle | running | review | result | failed
  const [thread,       setThread]       = useState([])                 // classify-agent chat items (Agent tab)
  const [sidebarTab,   setSidebarTab]   = useState('classes')          // 'classes' | 'agent'
  const [autoReviewOn, setAutoReviewOn] = useState(() => { try { return localStorage.getItem('classify-agent-autoreview') !== '0' } catch { return true } })
  const [reviewAfterClassify, setReviewAfterClassify] = useState(false)   // Classify finished: start the review
  const runRef = useRef(0)                                              // bumped on upload/classify: stale agent results are dropped
  const [showLeftovers, setShowLeftovers] = useState(false)
  const [verify, setVerify] = useState(null)                          // handle -> class the agent just applied, read back once
  const [agentPreview, setAgentPreview] = useState(null)   // Map handle -> colour on the canvas
  const applySeq = useRef(0)
  const floorAdmissionRef = useRef(null)
  const detectFloorRef = useRef(null)                                  // handleDetectFloor, for Retry (it is declared after the agent handlers)
  const floorPendingRef = useRef(null)
  const floorAnswerRef = useRef(null)
  const floorShowRef = useRef(null)
  const [roomPreview, setRoomPreview] = useState(null)
  const [inferredCorePreview, setInferredCorePreview] = useState(null)
  const chatRetry = useRef(null)                                       // the last answer sent, so Retry resends it
  const applyMeta = useRef(new Map())                                  // applyId -> { opIds, base, top }: the edit before the batch and the batch's latest edit, for Undo
  const agentRef = useRef(agent)
  agentRef.current = agent
  const [error,           setError]           = useState(null)
  const [saveError, setSaveError] = useState(null)
  const [saveConflict, setSaveConflict] = useState(null)
  const canvasSaver = useMemo(() => createCanvasSaver((payload, revision) => saveCanvasState(sessionId, payload, revision)), [sessionId])
  const [dragOver,        setDragOver]        = useState(false)
  const [popup,                setPopup]                = useState(null)
  const [showConfRemap,        setShowConfRemap]        = useState(false)

  const canvasRef        = useRef(null)
  const appliedScaleRef  = useRef(1)   // cumulative scale applied to raw coords (raw -> cm)
  const hydratingRef     = useRef(false)  // suppress autosave while a reopen is restoring state
  const restoredRevisionRef = useRef(null)
  const lastSavedRef     = useRef(null)   // saved payload used once to prime the reopened session
  const flushSaveRef     = useRef(null)   // latest autosave fn, called to flush before switching sessions
  const switchInFlightRef = useRef(false) // guards against overlapping handleUpload/handleReopen calls

  const [pdfState,      setPdfState]      = useState(null)  // { file, layers, layerVis, img, worldW, worldH }
  const [isPdfLoading,  setIsPdfLoading]  = useState(false)

  const [showRecent,    setShowRecent]    = useState(false)  // recent-sessions panel
  const [isReopening,   setIsReopening]   = useState(false)

  const pushEdit = useCallback((next) => {
    histRef.current = [...histRef.current.slice(-50), editRef.current]
    futRef.current  = []
    setEdit(recordEditorLineage(editRef.current, next))
  }, [])

  const undo = useCallback(() => {
    if (!histRef.current.length) return
    futRef.current = [editRef.current, ...futRef.current.slice(0, 49)]
    setEdit(histRef.current[histRef.current.length - 1])
    histRef.current = histRef.current.slice(0, -1)
  }, [])

  const redo = useCallback(() => {
    if (!futRef.current.length) return
    histRef.current = [...histRef.current, editRef.current]
    setEdit(futRef.current[0])
    futRef.current = futRef.current.slice(1)
  }, [])

  const scaleLocked = isClassifying || isDetectingFloor || agent.view === 'running'

  const handleApplyScale = useCallback((ws) => {
    if (scaleLocked) return
    if (!ws || ws <= 0 || ws === 1) return
    appliedScaleRef.current *= ws
    const scl = arr => arr?.map(e => scaleEntityCoords(e, ws))
    setUploadData(prev => !prev ? prev : {
      ...prev, original_manifest: scl(prev.original_manifest), cleaned_manifest: scl(prev.cleaned_manifest),
    })
    setClassifyData(prev => !prev ? prev : { ...prev, manifest: scl(prev.manifest) })
    setEdit(prev => ({
      ...prev,
      entityOverrides:   Object.fromEntries(Object.entries(prev.entityOverrides).map(([h,e]) => [h, scaleEntityCoords(e,ws)])),
      userPolylines:     prev.userPolylines.map(p => scaleEntityCoords(p, ws)),
      preprocessedEdits: scl(prev.preprocessedEdits) ?? [],
    }))
    setScaleFactor(1)
    setManuallyScaled(true)
    histRef.current = []; futRef.current = []; applyMeta.current.clear()
  }, [scaleLocked])

  const planSize = useMemo(() => planSizeM(uploadData?.cleaned_manifest?.length
    ? computeBounds(uploadData.cleaned_manifest) : null), [uploadData])
  const unitsHandlers = useMemo(() => ({
    onUnitChange: (cm) => handleApplyScale(cm / appliedScaleRef.current),
    onScaleByReference: () => { unitsReturnRef.current = true; setUnitsDialog(false); setActiveTool('scale') },
    onConfirm: () => { setUnitsConfirmed(true); setUnitsDialog(false) },
    onClose: () => { setUnitsDialog(false); setClassifyAfterUnits(false) },
  }), [handleApplyScale])

  const deletedSet   = useMemo(() => new Set(edit.deletedHandles), [edit.deletedHandles])
  const overridesMap = useMemo(() => new Map(Object.entries(edit.entityOverrides)), [edit.entityOverrides])

  const applyEdits = useCallback((manifest) => {
    if (!manifest?.length) return null
    return manifest
      .filter(e => !deletedSet.has(e.handle))
      .map(e => overridesMap.has(e.handle) ? overridesMap.get(e.handle) : e)
  }, [deletedSet, overridesMap])

  const originalManifest = useMemo(() => {
    const base = applyEdits(uploadData?.original_manifest) ?? []
    const mods = (edit.preprocessedMods ?? [])
      .filter(p => !deletedSet.has(p.handle))
      .map(p => overridesMap.has(p.handle) ? overridesMap.get(p.handle) : p)
    const all = [...base, ...mods]
    return all.length ? all : null
  }, [uploadData, applyEdits, edit.preprocessedMods, overridesMap, deletedSet])

  const preprocessedManifest = useMemo(() => {
    if (!uploadData) return null
    const base = applyEdits(uploadData.cleaned_manifest) ?? []
    const mods = (edit.preprocessedMods ?? [])
      .filter(p => !deletedSet.has(p.handle))
      .map(p => overridesMap.has(p.handle) ? overridesMap.get(p.handle) : p)
    const all  = applyProtectedLayers([...base, ...edit.preprocessedEdits, ...mods], edit.protectedLayers)
    return all.length ? all : null
  }, [uploadData, applyEdits, edit.preprocessedEdits, edit.preprocessedMods, edit.protectedLayers, overridesMap, deletedSet])

  const classifiedManifest = useMemo(() => {
    if (!classifyData) return null
    const handleClassOvr = edit.handleClassOverrides || {}
    const remapped = applyEdits(classifyData.manifest)?.map(e => {
      // Protected (Interior Design / Furniture) — classifier output is ignored
      const prot = edit.protectedLayers?.[e.handle]
      if (prot) return { ...e, layer: prot, isProtected: true, clientLayer: e.layer }
      // Per-handle class override from split action or "move to layer"  —  move to
      // new sublayer "{orig}_{class}" so hiding the source layer's group doesn't
      // also hide these (now reclassified) entities, which still share the same
      // original_layer_name used as the visibility key in the Classified view.
      const handleCls = handleClassOvr[e.handle]
      if (handleCls) return { ...e, layer: handleCls, source_layer_name: e.source_layer_name ?? e.original_layer_name, original_layer_name: reclassifiedLayerName(e.original_layer_name, handleCls) }
      // Explicit entityOverride (moves, etc.) takes priority
      if (overridesMap.has(e.handle)) return e
      // Layer-level remap
      const remap = edit.layerRemaps[e.original_layer_name]
      return remap ? { ...e, layer: remap } : e
    }) ?? []

    // preprocessedMods: entities from Explode/Join/Copy in Preprocess/Original view.
    // Resolve their DXF layer to the qbiq class the model assigned to that layer.
    const dxfLayerToClass = {}
    for (const e of classifyData.manifest) {
      if (e.original_layer_name && e.layer && !dxfLayerToClass[e.original_layer_name])
        dxfLayerToClass[e.original_layer_name] = e.layer
    }
    const prepMods = (edit.preprocessedMods ?? [])
      .filter(p => !deletedSet.has(p.handle))
      .map(p => {
        const g = overridesMap.has(p.handle) ? overridesMap.get(p.handle) : p
        const prot = edit.protectedLayers?.[p.handle]
        const qbiqClass = prot ?? edit.handleClassOverrides?.[p.handle] ?? edit.layerRemaps[p.layer] ?? dxfLayerToClass[p.layer] ?? '0'
        const base = { handle: p.handle, lineage: g.lineage, layer: qbiqClass, original_layer_name: p.layer, color: 256, ...(prot ? { isProtected: true, clientLayer: p.layer } : {}) }
        if (g.type === 'LINE') return { ...base, type: 'LINE', start: g.start, end: g.end }
        if (g.type === 'CIRCLE') return { ...base, type: 'CIRCLE', center: g.center, radius: g.radius }
        return { ...base, type: 'LWPOLYLINE', points: g.points, bulge: g.bulge, closed: g.closed ?? false }
      })

    const userPoly = edit.userPolylines
      .map((p, i) => {
        const handle = p.handle ?? `user-${i}`
        const g = overridesMap.has(handle) ? overridesMap.get(handle) : p
        // Prefer the source entity's real DXF sublayer identity (e.g. carried
        // over by Split) over the bare qbiq class name — a hand-drawn shape has
        // no such sublayer to inherit, so it falls back to the class name.
        const base = { handle, lineage: g.lineage, layer: g.layer, original_layer_name: g.original_layer_name ?? g.layer, color: 256, isUserDrawn: true }
        if (g.type === 'LINE') return { ...base, type: 'LINE', start: g.start, end: g.end }
        if (g.type === 'CIRCLE') return { ...base, type: 'CIRCLE', center: g.center, radius: g.radius }
        return { ...base, type: 'LWPOLYLINE', points: g.points, bulge: g.bulge, holes: g.holes, closed: g.closed ?? false }
      })
      .filter(p => !deletedSet.has(p.handle))
    return [...remapped, ...prepMods, ...userPoly]
  }, [classifyData, applyEdits, edit.layerRemaps, edit.handleClassOverrides, edit.userPolylines, edit.preprocessedMods, edit.protectedLayers, overridesMap, deletedSet])

  const floorSnapshot = useMemo(() => floorDrawingSnapshot(classifiedManifest, appliedScaleRef.current), [classifiedManifest])
  const floorSnapshotRef = useRef(floorSnapshot)
  floorSnapshotRef.current = floorSnapshot

  const manifest =
    activeView === 'classified'   ? classifiedManifest :
    activeView === 'preprocessed' ? preprocessedManifest :
                                    originalManifest
  const popupFocusHandles = useMemo(() => activeView === 'classified' && popup ? new Set(isolatePopupEntities(manifest, popup).map(e => e.handle)) : null, [manifest, activeView, popup])
  const manifestRef   = useRef(null)
  const activeViewRef = useRef(activeView)
  manifestRef.current   = manifest
  activeViewRef.current = activeView
  // O(1) handle -> entity lookup — avoids re-scanning the whole manifest for
  // every selected handle (was O(selection size * manifest size), the main
  // cause of the canvas freezing on large multi-selections).
  const manifestByHandle = useMemo(() => {
    const map = new Map()
    if (manifest) for (const e of manifest) map.set(e.handle, e)
    return map
  }, [manifest])
  const sourceByHandle = useMemo(() => new Map((classifyData?.manifest ?? []).map(e => [e.handle, e])), [classifyData])
  const selectionDetails = useMemo(() => selectionInfo(selectedHandles, manifestByHandle, sourceByHandle, activeView === 'classified'),
    [selectedHandles, manifestByHandle, sourceByHandle, activeView])
  const manifestByHandleRef = useRef(manifestByHandle)
  manifestByHandleRef.current = manifestByHandle
  const manifestFitKey = getManifestFitKey({ uploadData, sessionId, activeView })

  const handlePdfLayerToggle = useCallback(async (layerId) => {
    if (!pdfState) return
    const newVis = new Map(pdfState.layerVis)
    newVis.set(layerId, !newVis.get(layerId))
    setIsPdfLoading(true)
    try {
      const { renderPdfPage } = await import('./pdfUtils.js')
      const rendered = await renderPdfPage(pdfState.file, newVis)
      const img = new Image()
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = rendered.url })
      setPdfState(prev => ({ ...prev, layerVis: newVis, img }))
    } catch (e) { setError(e.message) }
    finally { setIsPdfLoading(false) }
  }, [pdfState])

  const handleRemovePdf = useCallback(() => {
    setPdfState(null)
  }, [])

  // Shared "start editing a new document" reset — used by both handleUpload and handleReopen.
  const resetEditorState = useCallback(() => {
    setClassifyData(null)
    setEdit(EMPTY_EDIT); histRef.current = []; futRef.current = []; applyMeta.current.clear()
    appliedScaleRef.current = 1
    setPopup(null); setFocusedLayer(null)
    setShiftSelectedLayers(new Set()); setSoloLayers(new Set())
    setScaleFactor(null); setDetectedUnits(null); setManuallyScaled(false); setUnitsConfirmed(false)
    runRef.current += 1
    setAgent({ view: 'idle' }); setThread([]); setAgentPreview(null); setShowLeftovers(false); setVerify(null)
  }, [])

  const handleUpload = useCallback(async (file) => {
    if (!file) return
    const ext = file.name.split('.').pop().toLowerCase()

    if (ext === 'pdf') {
      setIsPdfLoading(true); setError(null)
      try {
        const { extractPdfLayers, renderPdfPage } = await import('./pdfUtils.js')
        const layers = await extractPdfLayers(file)
        if (!layers) {
          setError('No AutoCAD layer data found in this PDF. Please upload an AutoCAD-exported PDF with layers.')
          return
        }
        const layerVis = new Map(layers.map(l => [l.id, l.visible]))
        const rendered = await renderPdfPage(file, layerVis)
        const img = new Image()
        await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = rendered.url })
        setPdfState({ file, layers, layerVis, img, worldW: rendered.pageWidth, worldH: rendered.pageHeight })
        setTimeout(() => canvasRef.current?.fitToScreen(), 50)
      } catch (e) { setError(e.message) }
      finally { setIsPdfLoading(false) }
      return
    }

    if (!['dxf', 'dwg'].includes(ext)) { setError('Please upload a .dxf, .dwg, or AutoCAD-exported PDF file'); return }
    if (switchInFlightRef.current) return
    switchInFlightRef.current = true
    setIsUploading(true); setError(null)
    if (await flushSaveRef.current?.() === false) {
      setIsUploading(false); switchInFlightRef.current = false; return
    }
    const unitsPromise = readDxfUnits(file)
    const form = new FormData(); form.append('file', file)
    try {
      const { session_id, job_id } = await uploadDxf(form)
      const data = await pollJobStatus(job_id, { label: 'Upload' })
      const finishApplyTrace = markPerf('upload.apply', {
        original: data.original_manifest?.length ?? 0,
        cleaned: data.cleaned_manifest?.length ?? 0,
      })
      const units = await unitsPromise
      const finishPrepareTrace = markPerf('upload.prepareData', { unit: units?.name ?? null })
      const prepared = prepareUploadDataUnits(data, units, session_id)
      finishPrepareTrace({
        original: prepared.data.original_manifest?.length ?? 0,
        cleaned: prepared.data.cleaned_manifest?.length ?? 0,
      })
      const finishLayersTrace = markPerf('upload.layers')
      const layers = getLayers(prepared.data.cleaned_manifest)
      finishLayersTrace({ count: layers.length })
      resetEditorState()
      lastSavedRef.current = null
      setSessionId(session_id)
      appliedScaleRef.current = prepared.appliedScale
      setDetectedUnits(units)
      setScaleFactor(prepared.scaleFactor)
      setUploadData(prepared.data)
      setActiveView('preprocessed')
      setVisibleLayers(new Set(layers))
      setSelectedHandles(new Set())
      finishApplyTrace()
    } catch (e) { setError(e.message) }
    finally { setIsUploading(false); switchInFlightRef.current = false }
  }, [])

  // Reload a past session's drawing onto the canvas. Mirrors the DXF upload success path:
  // the backend returns the stored manifests + $INSUNITS, so we scale exactly like an upload.
  // If the session has an autosaved canvas state, restore edits/classification on top.
  const handleReopen = useCallback(async (session, discardUnsaved = false) => {
    if (switchInFlightRef.current) return
    switchInFlightRef.current = true
    setShowRecent(false)
    setIsReopening(true); setError(null)
    if (!discardUnsaved && await flushSaveRef.current?.() === false) {
      setIsReopening(false); switchInFlightRef.current = false; return
    }
    hydratingRef.current = true
    try {
      const data = await reopenSession(session.session_id)
      const units = DXF_UNITS_TO_CM[data.insunits] != null
        ? { factor: DXF_UNITS_TO_CM[data.insunits], name: DXF_UNIT_NAMES[data.insunits] }
        : null
      const prepared = prepareUploadDataUnits(data, units, data.session_id)
      const canvas = data.canvas?.v === 1 ? data.canvas : null
      if (data.classification_available === false && canvas?.classifyData) {
        setError('Your saved edits are restored, but classification artifacts are unavailable. Run Classify again before floor detection or agent review.')
      }

      // Saved edit/classify coords live in canvas.appliedScale space; the fresh manifests are
      // only unit-scaled. Multiply by the residual (manual-scale remainder) to realign them.
      let preparedData = prepared.data
      let appliedScale = prepared.appliedScale
      let nextScaleFactor = prepared.scaleFactor
      if (canvas) {
        const residual = canvas.appliedScale / prepared.appliedScale
        if (residual !== 1) {
          const scl = arr => arr?.map(e => scaleEntityCoords(e, residual))
          preparedData = {
            ...preparedData,
            original_manifest: scl(preparedData.original_manifest),
            cleaned_manifest: scl(preparedData.cleaned_manifest),
          }
        }
        appliedScale = canvas.appliedScale
        nextScaleFactor = canvas.scaleFactor ?? null
      }

      resetEditorState()
      setPdfState(null)
      appliedScaleRef.current = appliedScale
      setSaveError(null); setSaveConflict(null)
      restoredRevisionRef.current = data.canvas_revision ?? 0
      setSessionId(data.session_id)
      setDetectedUnits(units)
      setScaleFactor(nextScaleFactor)
      setUploadData(preparedData)
      setSelectedHandles(new Set())
      if (canvas) {
        const restoredEdit = { ...EMPTY_EDIT, ...canvas.edit }   // tolerates older saves missing newer keys
        restoredEdit.entityOverrides = Object.fromEntries(Object.entries(restoredEdit.entityOverrides).map(([h, e]) => [h, editorEntity(e)]))
        restoredEdit.userPolylines = restoredEdit.userPolylines.map(editorEntity)
        const restoredClassify = canvas.classifyData && {
          ...canvas.classifyData, manifest: canvas.classifyData.manifest.map(editorEntity),
        }
        const restoredApproved = new Set(canvas.approvedLayers ?? [])
        const view = canvas.activeView === 'classified' && !restoredClassify ? 'original' : (canvas.activeView ?? 'original')
        const restoredVisible = new Set(canvas.visibleLayers ?? getLayers(preparedData.original_manifest))
        setEdit(restoredEdit)
        setClassifyData(restoredClassify)
        setApprovedLayers(restoredApproved)
        setActiveView(view)
        setVisibleLayers(restoredVisible)
        setUnitsConfirmed(!!canvas.unitsConfirmed)
        // Prime the dedupe key so the first post-hydration autosave run is a no-op.
        lastSavedRef.current = JSON.stringify(buildCanvasPayload({
          edit: restoredEdit, classifyData: restoredClassify, approvedLayers: restoredApproved,
          activeView: view, visibleLayers: restoredVisible, scaleFactor: nextScaleFactor, appliedScale,
          unitsConfirmed: canvas.unitsConfirmed,
        }))
      } else {
        lastSavedRef.current = null
        setActiveView('preprocessed')
        setVisibleLayers(new Set(getLayers(preparedData.cleaned_manifest)))
      }
    } catch (e) { setError(e.message) }
    finally { setIsReopening(false); hydratingRef.current = false; switchInFlightRef.current = false }
  }, [])

  // Autosave the canvas-edit state (debounced) so the session can be reopened later.
  useEffect(() => {
    if (!sessionId || !uploadData) { flushSaveRef.current = null; return }
    const payload = buildCanvasPayload({
      edit, classifyData, approvedLayers, activeView, visibleLayers, scaleFactor,
      appliedScale: appliedScaleRef.current, unitsConfirmed,
    })
    if (hydratingRef.current) return
    if (restoredRevisionRef.current !== null) {
      canvasSaver.setRevision(restoredRevisionRef.current)
      restoredRevisionRef.current = null
    }
    canvasSaver.update(payload, JSON.stringify(payload) === lastSavedRef.current)
    lastSavedRef.current = null
    const doSave = async () => {
      if (hydratingRef.current) return false
      try {
        await canvasSaver.flush()
        setSaveError(null); setSaveConflict(null)
        return true
      } catch (e) {
        setSaveConflict(e.status === 409 ? { revision: e.currentRevision } : null)
        setSaveError(`Changes are not saved: ${e.message}`)
        return false
      }
    }
    flushSaveRef.current = doSave
    const t = setTimeout(doSave, 2000)
    const onHide = () => { if (document.visibilityState === 'hidden') doSave() }
    const onLeave = e => {
      if (canvasSaver.dirty()) { e.preventDefault(); e.returnValue = '' }
    }
    document.addEventListener('visibilitychange', onHide)
    window.addEventListener('beforeunload', onLeave)
    return () => {
      clearTimeout(t)
      document.removeEventListener('visibilitychange', onHide)
      window.removeEventListener('beforeunload', onLeave)
    }
  }, [sessionId, uploadData, edit, classifyData, approvedLayers, activeView, visibleLayers, scaleFactor, unitsConfirmed, canvasSaver])

  const handleAutoReviewChange = useCallback((on) => {
    setAutoReviewOn(on)
    try { localStorage.setItem('classify-agent-autoreview', on ? '1' : '0') } catch { /* private mode */ }
  }, [])

  const handleClassify = useCallback(async () => {
    // Scale first: if the user never set it, ask now (AutoCAD UNITS); OK runs this classify.
    if (!unitsConfirmed && !manuallyScaled) { setClassifyAfterUnits(true); setUnitsDialog(true); return }
    if (agentRef.current.view === 'running' && agentRef.current.stage !== 'preclean') return
    runRef.current += 1
    setAgentPreview(null); setShowLeftovers(false)
    setAgent(isStaff ? { view: 'running', stage: 'preclean', steps: [], startedAt: Date.now() } : { view: 'idle' })
    setThread([])  // a new classification starts a new conversation
    setIsClassifying(true); setError(null)
    setEdit(e => ({ ...e, layerRemaps: {} }))
    setPopup(null); setFocusedLayer(null)
    setShiftSelectedLayers(new Set()); setSoloLayers(new Set())
    try {
      const { job_id } = await classifyRequest(sessionId, {
        cmPerUnit: appliedScaleRef.current,   // the scale the Units dialog shows; the user confirmed or set it above
      })
      const data = await pollJobStatus(job_id, {
        label: 'Classification',
        ...(isStaff ? { timeoutMs: AGENT_POLL_TIMEOUT_MS } : {}),
        onProgress: meta => setAgent(prev => prev.view === 'running' ? { ...prev, steps: meta.steps ?? prev.steps } : prev),
      })
      const pre = data.preclean_summary
      setAgent({ view: 'idle' })
      if (pre && (pre.dropped || pre.note)) setThread([{ id: 1, role: 'agent', kind: 'preclean', precleanSummary: pre }])
      // Post-process: join + close LINE entities in Columns layer into LWPOLYLINEs
      // Partition to keep protected entities out of column merging
      const protMap = editRef.current.protectedLayers ?? {}
      const protEnts = [], restEnts = []
      for (const e of data.manifest) {
        if (protMap[e.handle]) protEnts.push(e)
        else restEnts.push(e)
      }
      data.manifest = [...processColumnEntities(restEnts), ...protEnts]
      // Re-apply the same unit scale used on the upload so classify coords are in cm
      data.manifest = data.manifest.map(e => scaleEntityCoords(e, appliedScaleRef.current))
      // Clear any per-handle split overrides from a previous classify run
      setEdit(prev => ({ ...prev, handleClassOverrides: {} }))
      setClassifyData(data)
      if (isStaff && autoReviewOn) setReviewAfterClassify(true)
      setActiveView('classified')
      setApprovedLayers(new Set())  // reset approvals on each classify
      const groups = buildClassifiedGroups(data.manifest)
      const protClasses = Object.values(editRef.current.protectedLayers ?? {})
      setVisibleLayers(new Set([
        ...QLINE_ORDER,
        ...groups.flatMap(g => [
          g.qbiqClass,
          ...g.sublayers.map(s => s.name),
          ...g.qlineGroups.flatMap(q => [q.qbiqClass, ...q.sublayers.map(s => s.name)]),
        ]),
        ...protClasses,
      ]))
      setSelectedHandles(new Set())
    } catch (e) { setError(e.message); setAgent(prev => prev.view === 'running' ? { view: 'idle' } : prev) }
    finally { setIsClassifying(false) }
  }, [sessionId, isStaff, autoReviewOn, unitsConfirmed, manuallyScaled])

  // ── classify-agent feed (sidebar Agent tab): a read-only thread; the agent's changes are applied as ONE pushEdit ──
  const say = useCallback((item) => setThread(t => [...t, { id: t.length + 1, ...item }]), [])

  const applyAgentOps = useCallback((ops) => {
    const res = applyAgentResult(editRef.current, { ops }, new Set(ops.map(o => o.op_id)))
    if (res.applied.length) pushEdit(res.edit)
    setVisibleLayers(prev => new Set([...prev, ...res.sublayers, ...res.applied.map(o => o.to)]))
    return res
  }, [pushEdit])

  const pollAgent = useCallback(async (jobId, startedAt, stage, statusUrl) => {
    const run = runRef.current
    const live = () => run === runRef.current
    try {
      const res = await pollJobStatus(jobId, {
        label: 'classify-agent', timeoutMs: AGENT_POLL_TIMEOUT_MS, statusUrl,
        onProgress: meta => live() && setAgent(prev => prev.view === 'running' ? { ...prev, steps: meta.steps ?? prev.steps } : prev),
      })
      if (!live()) return   // the file or classification changed while the agent worked
      const ops = res.proposals ?? []
      const base = editRef.current
      const done = ops.length ? applyAgentOps(ops) : null
      const applyId = done?.applied.length ? ++applySeq.current : null
      if (applyId) {
        const opIds = done.applied.map(o => o.op_id)
        applyMeta.current.set(applyId, { opIds, base, top: done.edit })
        agentFeedback(sessionId, Object.fromEntries(opIds.map(id => [id, 'applied']))).catch(() => {})
        setVerify(done.expected)
      }
      say({ role: 'agent', text: res.reply, highlights: res.highlights, question: res.question,
            cost: res.stats?.cost_usd, turns: res.stats?.turns, steps: agentRef.current.steps ?? [],
            seconds: res.stats?.seconds, reset: res.stats?.reset, applyId, kept: done?.skipped.length ?? 0,
            changes: done?.applied.map(o => ({ op_id: o.op_id, kind: o.kind, layer: o.layer, to: o.to, count: o.count, why: o.why, handles: o.handles })) })
      if (stage === 'chat' && res.completion_status !== 'stopped' && chatRetry.current?.answer) {
        const answer = chatRetry.current.answer
        setThread(t => t.map(x => x.question?.id === answer.question_id ? { ...x, answered: answer.choice } : x))
      }
      setAgent({ view: 'idle' })
    } catch (e) {
      if (live()) setAgent({ view: 'failed', error: e.message, timedOut: !!e.timedOut, jobId, startedAt, stage, statusUrl })
    }
  }, [say, applyAgentOps, sessionId])

  const handleArchitect = useCallback(async (opts) => {
    if (agentRef.current.view === 'running') return
    say({ role: 'agent', text: opts?.auto ? 'Reviewing the classification…' : 'Reviewing the plan again…' })
    const startedAt = Date.now()
    setAgent({ view: 'running', stage: 'architect', steps: [], startedAt })
    setSidebarTab('agent')
    try {
      const { job_id, status_url } = await agentClassify(sessionId)
      await pollAgent(job_id, startedAt, 'architect', status_url)
    } catch (e) { setAgent({ view: 'failed', error: e.message, stage: 'architect' }) }
  }, [sessionId, pollAgent, say])

  // The only thing the user sends is an answer to one of the agent's questions.
  const sendChat = useCallback(async (message, answer, retryRequest) => {
    if (agentRef.current.view === 'running') return
    const startedAt = Date.now()
    setAgent({ view: 'running', stage: 'chat', steps: [], startedAt })
    const e = editRef.current
    const request = retryRequest ?? {
      message, answer, client_job_ref: crypto.randomUUID(),
      edits: { layer_remaps: e.layerRemaps, handle_class_overrides: e.handleClassOverrides, protected_layers: e.protectedLayers },
    }
    chatRetry.current = { message, answer, request }
    try {
      const { job_id, status_url } = await agentChat(sessionId, request)
      await pollAgent(job_id, startedAt, 'chat', status_url)
    } catch (err) { setAgent({ view: 'failed', error: err.message, stage: 'chat' }) }
  }, [sessionId, pollAgent])

  // The review starts by itself once Classify has landed (staff, when "Auto-review" is on).
  useEffect(() => {
    if (reviewAfterClassify && classifyData && !isClassifying) { setReviewAfterClassify(false); handleArchitect({ auto: true }) }
  }, [reviewAfterClassify, classifyData, isClassifying, handleArchitect])

  // Preview is rendering only: it never becomes a selection (no Move/Erase toolbar during review).
  const previewOps = useCallback((ops) => {
    setAgentPreview(ops?.length
      ? new Map(ops.flatMap(o => (o.handles ?? []).map(h => [h, o.color ?? CLASS_COLORS[o.to] ?? '#6b7280'])))
      : null)
  }, [])
  const canvasSelection = useMemo(() => agentPreview ? new Set(agentPreview.keys()) : selectedHandles,
    [agentPreview, selectedHandles])

  const agentHandlers = useMemo(() => ({
    onRetry: () => {
      const a = agentRef.current
      setAgent({ view: 'idle' })
      const r = chatRetry.current
      a.stage === 'floor' ? detectFloorRef.current?.() : a.stage === 'chat' && r ? sendChat(r.message, r.answer, r.request) : handleArchitect()
    },
    onRepoll: () => {
      const a = agentRef.current
      setAgent({ view: 'running', stage: a.stage, steps: [], startedAt: a.startedAt ?? Date.now() })
      pollAgent(a.jobId, a.startedAt, a.stage, a.statusUrl)
    },
    onStop: () => { floorAdmissionRef.current?.abort(); agentStop(sessionId).catch(err => setError(err.message)) },
    onShow: (handles) => previewOps(handles ? [{ handles, color: '#2563eb' }] : null),
    onShowQuestion: (handles) => { previewOps([{ handles, color: '#2563eb' }]); canvasRef.current?.zoomToHandles?.(handles) },
    onShowRoom: item => floorShowRef.current?.(item),
    onAnswerText: (item, choice) => {
      if (item.question.kind === 'floor_room') return floorAnswerRef.current?.(item, choice)
      sendChat(choice, { question_id: item.question.id, choice })
    },
    onAnswer: (item, choice) => {
      const q = item.question
      previewOps(null)
      if (choice !== 'Skip') {
        const ops = answerOps(q, choice)
        const res = applyAgentOps(ops)
        agentFeedback(sessionId, Object.fromEntries(res.applied.map(o => [o.op_id, 'applied']))).catch(() => {})
      }
      sendChat(choice, { question_id: q.id, choice })
    },
    // The dropdown on a change: put its entities in another class, on the fly (one more undo step).
    onChangeClass: (item, change, cls) => {
      if (cls === change.to) return
      const cur = editRef.current
      const handles = change.kind === 'classify_layer' ? [] : freeChangeHandles(cur, change)
      if (change.kind !== 'classify_layer' && !handles.length) return
      const next = change.kind === 'classify_layer'
        ? { ...cur, layerRemaps: { ...cur.layerRemaps, [change.layer]: cls } }
        : { ...cur, handleClassOverrides: { ...cur.handleClassOverrides, ...Object.fromEntries(handles.map(h => [h, cls])) } }
      const meta = applyMeta.current.get(item.applyId)
      pushEdit(next)
      if (meta?.top === cur) meta.top = next
      setVisibleLayers(prev => new Set([...prev, cls, reclassifiedLayerName(change.layer, cls)]))
      setThread(t => t.map(x => x.id === item.id
        ? { ...x, changes: x.changes.map(c => c.op_id === change.op_id ? { ...c, to: cls } : c) } : x))
      agentFeedback(sessionId, { [change.op_id]: `changed_to:${cls}` }).catch(() => {})
    },
    onUndoApply: (item) => {
      const meta = applyMeta.current.get(item.applyId)
      const back = undoBatch(histRef.current, editRef.current, meta)
      if (!back) {
        say({ role: 'agent', text: 'You made other edits after this. Undo those first (Ctrl+Z), then this one.' })
        return
      }
      futRef.current = [editRef.current, ...futRef.current.slice(0, 49)]
      histRef.current = back.hist
      setEdit(back.edit)
      agentFeedback(sessionId, Object.fromEntries(meta.opIds.map(id => [id, 'undone']))).catch(() => {})
      setThread(t => t.map(x => x.id === item.id ? { ...x, undone: true } : x))
    },
    onPreviewPreclean: (cls, layer) => {
      if (!cls) return previewOps(null)
      const handles = (classifiedManifest ?? []).filter(e => e.source === 'preclean' && e.layer === cls
        && (e.original_layer_name === layer || e.original_layer_name?.startsWith(`${layer}_`))).map(e => e.handle)
      previewOps([{ handles, to: cls }])
    },
  }), [previewOps, undo, pushEdit, handleArchitect, sendChat, pollAgent, say, applyAgentOps, classifiedManifest, sessionId])

  // Read back after the agent's changes: every entity it moved must show its new class.
  useEffect(() => {
    if (!verify || !classifiedManifest) return
    const wrong = {}
    for (const e of classifiedManifest) {
      if (verify[e.handle] && e.layer !== verify[e.handle]) wrong[e.original_layer_name] = (wrong[e.original_layer_name] ?? 0) + 1
    }
    setVerify(null)
    const layers = Object.entries(wrong)
    if (layers.length) say({ role: 'agent', text: `Some changes did not show on the canvas: ${layers.map(([l, n]) => `${l} (${n})`).join(', ')}.` })
  }, [verify, classifiedManifest, say])

  // Modal's leftovers: entities still 0 / Untagged, tinted on the canvas on demand (no server call).
  const leftoverHandles = useMemo(() => (classifiedManifest ?? [])
    .filter(e => e.layer === '0' || e.layer === 'Untagged').map(e => e.handle), [classifiedManifest])
  useEffect(() => {
    if (sidebarTab !== 'agent') setShowLeftovers(false)
    setAgentPreview(showLeftovers && leftoverHandles.length
      ? new Map(leftoverHandles.map(h => [h, '#f59e0b'])) : prev => (showLeftovers ? prev : null))
  }, [showLeftovers, leftoverHandles, sidebarTab])

  const precleanLayers = useMemo(() => new Set((classifiedManifest ?? [])
    .filter(e => e.source === 'preclean').map(e => e.original_layer_name)), [classifiedManifest])

  useEffect(() => {
    if (classifyAfterUnits && unitsConfirmed && !unitsDialog) { setClassifyAfterUnits(false); handleClassify() }
  }, [classifyAfterUnits, unitsConfirmed, unitsDialog, handleClassify])

  const handleTabChange = useCallback((view) => {
    setActiveView(view); setSelectedHandles(new Set()); setPopup(null); setFocusedLayer(null)
    canvasRef.current?.cancelTool()
    if (view === 'classified' && classifiedManifest) {
      const groups = buildClassifiedGroups(classifiedManifest)
      const protCls = Object.values(editRef.current.protectedLayers ?? {})
      setVisibleLayers(new Set([
        ...QLINE_ORDER,
        ...groups.flatMap(g => [
          g.qbiqClass,
          ...g.sublayers.map(s => s.name),
          ...g.qlineGroups.flatMap(q => [q.qbiqClass, ...q.sublayers.map(s => s.name)]),
        ]),
        ...protCls,
      ]))
    } else if (view === 'preprocessed' && uploadData) {
      const layers = getLayers([...(applyEdits(uploadData.cleaned_manifest) ?? []), ...editRef.current.preprocessedEdits])
      const protCls = Object.values(editRef.current.protectedLayers ?? {})
      setVisibleLayers(new Set([...layers, ...protCls]))
    } else if (view === 'original' && uploadData) {
      setVisibleLayers(new Set(getLayers(applyEdits(uploadData.original_manifest) ?? [])))
    }
  }, [classifiedManifest, uploadData, applyEdits])

  // For each source layer × qbiq class: count of entities the model matched to that class
  // Uses same two-tier logic as handleChipClick so chip counts match what gets selected
  const naturalDistribution = useMemo(() => {
    if (!classifyData?.manifest) return {}

    // Group entities by source layer
    const byLayer = {}
    for (const e of classifyData.manifest) {
      const orig = e.original_layer_name || e.layer
      if (!byLayer[orig]) byLayer[orig] = []
      byLayer[orig].push(e)
    }

    const result = {}
    for (const [orig, entities] of Object.entries(byLayer)) {
      // Collect all qbiq classes that appear in any entity's class_probabilities
      const allClasses = new Set(
        entities.flatMap(e => e.class_probabilities ? Object.keys(e.class_probabilities) : [e.layer])
      )
      result[orig] = {}
      for (const cls of allClasses) {
        // Primary count: entities where cls is their top probability class
        let matching = entities.filter(e => {
          if (!e.class_probabilities) return e.layer === cls
          const best = Object.entries(e.class_probabilities).sort((a, b) => b[1] - a[1])[0]?.[0]
          return best === cls
        })
        // Fallback: assign each entity to its best non-Untagged class
        if (matching.length === 0) {
          matching = entities.filter(e => {
            if (!e.class_probabilities) return false
            const best = Object.entries(e.class_probabilities)
              .filter(([k]) => k !== '0')
              .sort((a, b) => b[1] - a[1])[0]?.[0]
            return best === cls
          })
        }
        if (matching.length > 0) {
          const scores = matching.map(e => e.class_probabilities?.[cls] ?? e.score).filter(s => s != null)
          result[orig][cls] = {
            count: matching.length,
            avgScore: scores.length ? Math.round(scores.reduce((a,b)=>a+b,0)/scores.length*100) : null,
          }
        }
      }
    }
    return result
  }, [classifyData])

  const handleRemap = useCallback((origLayerName, newQbiqClass) => {
    pushEdit({ ...editRef.current, layerRemaps: { ...editRef.current.layerRemaps, [origLayerName]: newQbiqClass } })
    setVisibleLayers(prev => new Set([...prev, origLayerName]))
    setPopup(null)
    setPendingRemap(null)
  }, [pushEdit])

  // ── Protected layers: assign/clear from selection bar (Preprocessed view) ────
  const handleProtectAssign = useCallback((cls) => {
    const cur = editRef.current
    const next = { ...(cur.protectedLayers ?? {}) }
    for (const h of selectedHandles) {
      if (cls) next[h] = cls
      else delete next[h]
    }
    pushEdit({ ...cur, protectedLayers: next })
    if (cls) setVisibleLayers(prev => new Set([...prev, cls]))
    setLayerDropdown.close()
    setSelectedHandles(new Set())
  }, [pushEdit, selectedHandles, setLayerDropdown.close])

  // ── Apply layer split ─────────────────────────────────────────────────────────
  // assignments: { targetClass: [handles] } — each handle gets its target class override.
  // Unchecked / unassigned geoms keep their existing layer assignment.
  const handleApplySplit = useCallback((origLayerName, assignments) => {
    const newOverrides = { ...editRef.current.handleClassOverrides }
    for (const [targetCls, handles] of Object.entries(assignments)) {
      for (const handle of handles) {
        newOverrides[handle] = targetCls
      }
    }
    pushEdit({ ...editRef.current, handleClassOverrides: newOverrides })
    const splitLayerNames = Object.keys(assignments).map(cls => reclassifiedLayerName(origLayerName, cls))
    setVisibleLayers(prev => new Set([...prev, origLayerName, ...splitLayerNames]))
    setPopup(null)
    setPendingRemap(null)
    setSelectedHandles(new Set())
    setFocusedLayer(null)
  }, [pushEdit])

  const handleChipClick = useCallback((origLayerName, cls) => {
    if (!classifyData?.manifest) return
    const sourceEntities = classifyData.manifest.filter(e =>
      (e.original_layer_name || e.layer) === origLayerName
    )

    // Primary: entities where cls is their individual top-probability class
    let matching = sourceEntities.filter(e => {
      if (!e.class_probabilities) return e.layer === cls
      const best = Object.entries(e.class_probabilities).sort((a, b) => b[1] - a[1])[0]?.[0]
      return best === cls
    })

    // Fallback: assign each entity to its best *non-Untagged* class so different chips
    // highlight different (non-overlapping) entities
    if (matching.length === 0) {
      matching = sourceEntities.filter(e => {
        if (!e.class_probabilities) return false
        const best = Object.entries(e.class_probabilities)
          .filter(([k]) => k !== '0')
          .sort((a, b) => b[1] - a[1])[0]?.[0]
        return best === cls
      })
    }

    setSelectedHandles(new Set(matching.map(e => e.handle)))
    setPendingRemap({ origLayerName, newClass: cls })
  }, [classifyData])

  const handleConfidenceRemap = useCallback((highThresh, lowThresh) => {
    if (!classifyData) return
    // layer_split_data keys are original DXF layer names; fused_probs is the
    // PoE fusion of layer + entity classifier  —  available for every layer,
    // unlike e.score which is null for non-homogenous layers.
    const splitData = classifyData.layer_split_data || {}
    const newRemaps  = { ...editRef.current.layerRemaps }
    const newVisible = new Set(visibleLayers)

    for (const [layerName, data] of Object.entries(splitData)) {
      const entries = Object.entries(data.fused_probs || {}).sort((a, b) => b[1] - a[1])
      if (!entries.length) continue
      const [topClass, topProb] = entries[0]
      const pct = topProb * 100

      if (pct < lowThresh) {
        newRemaps[layerName] = '0'
        newVisible.add(layerName)
      } else if (pct >= highThresh) {
        newRemaps[layerName] = topClass
        newVisible.add(layerName)
      }
      // Medium range -> no change
    }

    pushEdit({ ...editRef.current, layerRemaps: newRemaps })
    setVisibleLayers(newVisible)
  }, [classifyData, visibleLayers, pushEdit])

  const handleClickSelect = useCallback((handle, isAdditive) => {
    if (handle === null && !isAdditive) setShiftSelectedLayers(new Set())
    setSelectedHandles(prev => {
      if (handle === null) return isAdditive ? prev : new Set()
      if (isAdditive) {
        const next = new Set(prev)
        next.has(handle) ? next.delete(handle) : next.add(handle)
        return next
      }
      return prev.has(handle) && prev.size === 1 ? new Set() : new Set([handle])
    })
  }, [])

  const handleBoxSelectComplete = useCallback((handles, isAdditive) => {
    setSelectedHandles(prev => isAdditive ? new Set([...prev, ...handles]) : handles)
  }, [])

  const handleFenceComplete = useCallback((handles, subtractMode) => {
    if (subtractMode) {
      setSelectedHandles(prev => { const n = new Set(prev); handles.forEach(h => n.delete(h)); return n })
    } else {
      setSelectedHandles(prev => new Set([...prev, ...handles]))
    }
  }, [])

  // Look up entity by handle — entityOverrides first, then manifest, then user arrays (always fresh via refs)
  const resolveEnt = useCallback((h) => {
    const cur = editRef.current
    return cur.entityOverrides[h]
      ?? manifestByHandleRef.current.get(h)
      ?? cur.userPolylines?.find(e => e.handle === h)
      ?? cur.preprocessedMods?.find(e => e.handle === h)
      ?? null
  }, [])

  // ── Move ──────────────────────────────────────────────────────────────────────
  const handleMoveComplete = useCallback((handles, dx, dy) => {
    const newOverrides = { ...editRef.current.entityOverrides }
    for (const h of handles) {
      const base = resolveEnt(h)
      if (base) newOverrides[h] = moveEntity(stripProtection(base), dx, dy)
    }
    pushEdit({ ...editRef.current, entityOverrides: newOverrides })
    mirrorCycleRef.current.selKey = null
  }, [pushEdit, resolveEnt])

  const copyBufferRef = useRef([])

  // Persist newly-created entities to the right bucket based on the active view.
  // Classified view -> userPolylines (always blue / isUserDrawn).
  // Preprocess / Original view -> preprocessedMods (resolved to qbiq class after Classify).
  function persistNewEnts(newEnts) {
    if (!newEnts.length) return
    const cur = editRef.current
    if (activeViewRef.current === 'classified') {
      pushEdit({ ...cur, userPolylines: [...cur.userPolylines, ...newEnts] })
      const newLayers = [...new Set(newEnts.map(e => e.layer).filter(Boolean))]
      if (newLayers.length) setVisibleLayers(prev => new Set([...prev, ...newLayers]))
    } else {
      pushEdit({ ...cur, preprocessedMods: [...(cur.preprocessedMods ?? []), ...newEnts] })
    }
  }

  // Build a minimal clean entity suitable for userPolylines/preprocessedMods storage
  function makeUserEnt(src, handle) {
    if (!src) return null
    const s = stripProtection(src)
    const lineage = derivedFrom('copy', [src.handle])
    if (s.type === 'LWPOLYLINE' && s.points?.length)
      return { type: 'LWPOLYLINE', points: s.points.map(p => [...p]), closed: s.closed ?? false, layer: s.layer, handle, lineage }
    if (s.type === 'LINE' && s.start && s.end)
      return { type: 'LINE', start: [...s.start], end: [...s.end], layer: s.layer, handle, lineage }
    if (s.type === 'CIRCLE' && s.center)
      return { type: 'CIRCLE', center: [...s.center], radius: s.radius, layer: s.layer, handle, lineage }
    return null
  }
  const placeCopies = useCallback((sourceEnts, dx, dy) => {
    const ts = Date.now()
    const protInherit = {}
    const curProtMap = editRef.current.protectedLayers ?? {}
    const newPolys = sourceEnts.map((src, i) => {
      const moved = moveEntity(src, dx, dy)
      const ent = makeUserEnt(moved, `copy-${ts}-${i}`)
      if (ent && src.handle && curProtMap[src.handle]) protInherit[ent.handle] = curProtMap[src.handle]
      return ent
    }).filter(Boolean)
    if (!newPolys.length) return
    if (Object.keys(protInherit).length) {
      const cur = editRef.current
      const view = activeViewRef.current
      const nextProt = { ...curProtMap, ...protInherit }
      if (view === 'classified') {
        pushEdit({ ...cur, userPolylines: [...cur.userPolylines, ...newPolys], protectedLayers: nextProt })
      } else {
        pushEdit({ ...cur, preprocessedMods: [...(cur.preprocessedMods ?? []), ...newPolys], protectedLayers: nextProt })
      }
    } else {
      persistNewEnts(newPolys)
    }
    setSelectedHandles(new Set(newPolys.map(p => p.handle)))
  }, [pushEdit])

  const handleRotateComplete = useCallback((handles, cx, cy, angle) => {
    const newOverrides = { ...editRef.current.entityOverrides }
    for (const h of handles) {
      const base = resolveEnt(h)
      if (base) newOverrides[h] = rotateEntity(stripProtection(base), cx, cy, angle)
    }
    pushEdit({ ...editRef.current, entityOverrides: newOverrides })
    mirrorCycleRef.current.selKey = null
  }, [pushEdit, resolveEnt])

  // ── Pick-point handler for Move/Copy/Rotate two-click flows ───────────────────
  const handlePointPick = useCallback((wx, wy, shiftKey = false) => {
    if (pendingMove) {
      if (pendingMove.step === 'base') {
        setPendingMove({ ...pendingMove, step: 'dest', baseWX: wx, baseWY: wy })
      } else {
        const dx = wx - pendingMove.baseWX, dy = wy - pendingMove.baseWY
        const { mode, handles, pasteEnts } = pendingMove
        setPendingMove(null)
        if (mode === 'move') {
          handleMoveComplete(handles, dx, dy)
        } else {
          placeCopies(pasteEnts, dx, dy)
        }
      }
    } else if (pendingTransform) {
      if (pendingTransform.step === 'center') {
        setPendingTransform({ ...pendingTransform, step: 'angle', cx: wx, cy: wy })
      } else {
        let angle = Math.atan2(wy - pendingTransform.cy, wx - pendingTransform.cx)
        if (shiftKey) angle = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4)
        handleRotateComplete(pendingTransform.handles, pendingTransform.cx, pendingTransform.cy, angle)
        setPendingTransform(null)
      }
    }
  }, [pendingMove, pendingTransform, handleMoveComplete, placeCopies, handleRotateComplete])

  const startMove = useCallback(() => {
    if (!selectedHandles.size) return
    const ents = [...selectedHandles].map(h => resolveEnt(h)).filter(Boolean)
    setPendingMove({ mode: 'move', step: 'base', handles: new Set(selectedHandles), pasteEnts: ents })
  }, [selectedHandles])

  const startCopy = useCallback(() => {
    if (!selectedHandles.size) return
    const ents = [...selectedHandles].map(h => resolveEnt(h)).filter(Boolean)
    setPendingMove({ mode: 'copy', step: 'base', handles: new Set(selectedHandles), pasteEnts: ents })
  }, [selectedHandles])

  const startRotate = useCallback(() => {
    if (!selectedHandles.size) return
    const ents = [...selectedHandles].map(h => resolveEnt(h)).filter(Boolean)
    setPendingTransform({ mode: 'rotate', step: 'center', handles: new Set(selectedHandles), entities: ents })
  }, [selectedHandles])

  const handleCycleMirror = useCallback(() => {
    if (!selectedHandles.size) return
    const selKey = [...selectedHandles].sort().join(',')
    const cycle = mirrorCycleRef.current
    if (cycle.selKey !== selKey) {
      const orig = {}
      for (const h of selectedHandles) orig[h] = resolveEnt(h)
      cycle.origOverrides = orig
      cycle.selKey = selKey
      cycle.step = 0
    }
    cycle.step = (cycle.step + 1) % 4
    const ents = Object.values(cycle.origOverrides).filter(Boolean)
    const b = computeBounds(ents)
    if (!b || b.minX === Infinity) return
    const cx = (b.minX + b.maxX) / 2, cy = (b.minY + b.maxY) / 2
    // 4 states: 0=original  1=H-flip  2=V-flip  3=H+V-flip (=180° rotation)
    const hflip = cycle.step === 1 || cycle.step === 3
    const vflip = cycle.step === 2 || cycle.step === 3
    const newOverrides = { ...editRef.current.entityOverrides }
    for (const [h, orig] of Object.entries(cycle.origOverrides)) {
      if (!orig) continue
      let ent = stripProtection(orig)
      if (hflip) ent = mirrorEntity(ent, cx, cy - 1, cx, cy + 1) // vertical axis → left/right flip
      if (vflip) ent = mirrorEntity(ent, cx - 1, cy, cx + 1, cy) // horizontal axis → top/bottom flip
      newOverrides[h] = ent
    }
    pushEdit({ ...editRef.current, entityOverrides: newOverrides })
  }, [selectedHandles, pushEdit, resolveEnt])

  const handleJoin = useCallback(() => {
    if (!manifest || selectedHandles.size < 2) return
    const chains = [...selectedHandles].map(h => {
      const e = resolveEnt(h)
      if (!e) return null
      if (e.type === 'LWPOLYLINE' && e.points?.length >= 2) return { pts: e.points.map(p => [...p]), bulge: e.bulge, handle: e.handle, layer: e.layer }
      if (e.type === 'LINE' && e.start && e.end) return { pts: [[...e.start], [...e.end]], handle: e.handle, layer: e.layer }
      return null
    }).filter(Boolean)
    if (chains.length < 2) return
    const ptDist = (a, b) => Math.hypot(a[0]-b[0], a[1]-b[1])
    const tol = Math.max(1.0, chains.reduce((mx, c) => {
      const len = ptDist(c.pts[0], c.pts[c.pts.length - 1])
      return Math.max(mx, len * 0.001)
    }, 0))  // 0.1% of longest span, min 1 unit
    const joined = joinPolylines(chains, tol)
    if (!joined) {
      alert('Selected entities don\'t share endpoints  —  cannot join.'); return
    }
    const newHandle = `join-${Date.now()}`
    const newEnt = { type: 'LWPOLYLINE', ...joined, closed: false, layer: chains[0].layer, handle: newHandle, lineage: derivedFrom('join', chains.map(c => c.handle)) }
    const cur = editRef.current
    const nextDeleted = [...new Set([...cur.deletedHandles, ...chains.map(c => c.handle)])]
    // Inherit protection from any protected source
    const protMap = cur.protectedLayers ?? {}
    const protCls = chains.map(c => protMap[c.handle]).find(Boolean)
    const nextProt = protCls ? { ...protMap, [newHandle]: protCls } : protMap
    if (activeViewRef.current === 'classified') {
      pushEdit({ ...cur, deletedHandles: nextDeleted, userPolylines: [...cur.userPolylines, newEnt], protectedLayers: nextProt })
    } else {
      pushEdit({ ...cur, deletedHandles: nextDeleted, preprocessedMods: [...(cur.preprocessedMods ?? []), newEnt], protectedLayers: nextProt })
    }
    setSelectedHandles(new Set([newHandle]))
  }, [manifest, selectedHandles, pushEdit])

  // ── Split ──────────────────────────────────────────────────────────────────────
  // Right-click "Split" on a vertex — the geometric inverse of Join. Deletes the
  // source polyline and replaces it with the pieces `splitPolylineAtVertex` computes
  // (two pieces for an open polyline, one re-opened piece for a closed one).
  // Invoked from a midpoint diamond instead (`midpointSeg` set, `vi` unused), it
  // first inserts a vertex there — same as "Add Vertex" — then splits on it. A
  // LINE has no vertex of its own, so it only supports the midpoint-diamond path,
  // normalized here to the same 2-point/single-segment shape used elsewhere.
  const handleSplitVertex = useCallback((handle, vi, midpointSeg) => {
    const e = resolveEnt(handle)
    if (!e) return
    const isLine = e.type === 'LINE' && e.start && e.end
    if (!isLine && (e.type !== 'LWPOLYLINE' || !e.points?.length)) return
    let points = isLine ? [e.start, e.end] : e.points
    let bulge = isLine ? [0] : e.bulge
    const closed = isLine ? false : e.closed
    let splitVi = vi
    if (midpointSeg !== undefined) {
      const inserted = insertVertexAtSegment(points, bulge, midpointSeg, closed)
      points = inserted.points; bulge = inserted.bulge; splitVi = midpointSeg + 1
    }
    const pieces = splitPolylineAtVertex(points, bulge, splitVi, closed)
    const stamp = Date.now()
    const newEnts = pieces.map((p, i) => ({
      type: 'LWPOLYLINE', ...p, layer: e.layer, original_layer_name: e.original_layer_name, handle: `split-${stamp}-${i}`, lineage: derivedFrom('split', [handle]),
    }))
    const cur = editRef.current
    const protMap = cur.protectedLayers ?? {}
    const prot = protMap[handle]
    const nextProt = prot ? { ...protMap, ...Object.fromEntries(newEnts.map(n => [n.handle, prot])) } : protMap
    const nextDeleted = [...new Set([...cur.deletedHandles, handle])]
    if (activeViewRef.current === 'classified') {
      pushEdit({ ...cur, deletedHandles: nextDeleted, userPolylines: [...cur.userPolylines, ...newEnts], protectedLayers: nextProt })
    } else {
      pushEdit({ ...cur, deletedHandles: nextDeleted, preprocessedMods: [...(cur.preprocessedMods ?? []), ...newEnts], protectedLayers: nextProt })
    }
    setSelectedHandles(new Set(newEnts.map(n => n.handle)))
  }, [pushEdit, resolveEnt])

  const handleExplode = useCallback(() => {
    if (!manifest || !selectedHandles.size) return
    const newLines = [], toDelete = []
    const protInherit = {}
    const ts = Date.now()
    let seg = 0
    const curProtMap = editRef.current.protectedLayers ?? {}
    for (const h of selectedHandles) {
      const e = resolveEnt(h)
      if (!e || e.type !== 'LWPOLYLINE' || !e.points?.length) continue
      const pieces = explodePolyline(e)
      if (!pieces.length) continue
      toDelete.push(e.handle)
      const srcCls = curProtMap[e.handle]
      for (const piece of pieces) {
        const newHandle = `exp-${ts}-${seg++}`
        newLines.push({ ...piece, layer: e.layer, handle: newHandle, lineage: derivedFrom('explode', [e.handle]) })
        if (srcCls) protInherit[newHandle] = srcCls
      }
    }
    if (!newLines.length) return
    const cur = editRef.current
    const nextDeleted = [...new Set([...cur.deletedHandles, ...toDelete])]
    const nextProt = Object.keys(protInherit).length ? { ...(cur.protectedLayers ?? {}), ...protInherit } : (cur.protectedLayers ?? {})
    if (activeViewRef.current === 'classified') {
      pushEdit({ ...cur, deletedHandles: nextDeleted, userPolylines: [...cur.userPolylines, ...newLines], protectedLayers: nextProt })
    } else {
      pushEdit({ ...cur, deletedHandles: nextDeleted, preprocessedMods: [...(cur.preprocessedMods ?? []), ...newLines], protectedLayers: nextProt })
    }
    setSelectedHandles(new Set(newLines.map(p => p.handle)))
  }, [manifest, selectedHandles, pushEdit])

  const handleErase = useCallback(() => {
    if (!selectedHandles.size) return
    pushEdit({ ...editRef.current, deletedHandles: [...new Set([...editRef.current.deletedHandles, ...selectedHandles])] })
    setSelectedHandles(new Set())
  }, [selectedHandles, pushEdit])

  const handleTransfer = useCallback(() => {
    if (!selectedHandles.size || !uploadData) return
    const ts = Date.now()
    const toTransfer = uploadData.original_manifest
      .filter(e => selectedHandles.has(e.handle))
      .map((e, i) => ({ ...e, handle: `transfer-${e.handle}-${ts}-${i}`, isTransferred: true, lineage: derivedFrom('copy', [e.handle]) }))
    if (!toTransfer.length) return
    pushEdit({ ...editRef.current, preprocessedEdits: [...editRef.current.preprocessedEdits, ...toTransfer] })
    setSelectedHandles(new Set())
    handleTabChange('preprocessed')
  }, [selectedHandles, uploadData, pushEdit, handleTabChange])

  const coreEditPendingRef = useRef(false)
  const handleDrawnEntity = useCallback(async (entry) => {
    if (coreEditPendingRef.current) {
      setError('A core edit is still saving. Please wait and draw again.')
      return
    }
    const cur = editRef.current
    const run = runRef.current
    const changesFloor = entry.layer === 'Core Q-Line' && entry.points?.length >= 3 && cur.userPolylines.some(p => p.layer === 'Flood')
    if (changesFloor) coreEditPendingRef.current = true
    try {
      const next = await appendDrawnEntity(cur, entry, body => previewFloorEdit(sessionId, body), crypto.randomUUID())
      if (run !== runRef.current) return
      if (cur !== editRef.current) {
        setError('The drawing changed while saving. Please draw the polygon again.')
        return
      }
      pushEdit(next)
      setVisibleLayers(prev => prev.has(entry.layer) ? prev : new Set([...prev, entry.layer]))
    } catch (err) {
      if (run === runRef.current) setError(`Core creation failed. ${err.message}`)
    } finally {
      if (changesFloor) coreEditPendingRef.current = false
    }
  }, [pushEdit, sessionId])

  const handlePolylineComplete = useCallback(({ points, layer }) => {
    const isFloodLayer = layer === 'Flood' || layer === 'Outdoor Flood'
    const entry = isFloodLayer
      ? { points, layer, closed: true, handle: `drawn-flood-${Date.now()}` }
      : { points, layer, handle: `drawn-poly-${Date.now()}` }
    return handleDrawnEntity(entry)
  }, [handleDrawnEntity])

  const handleShapeComplete = useCallback(entity => {
    return handleDrawnEntity({ ...entity, layer: entity.layer || activeLayer })
  }, [handleDrawnEntity, activeLayer])

  const [floorStep, setFloorStep] = useState('')    // what the floor agent is doing now, from the job's progress
  const [floorResultNote, setFloorResultNote] = useState(null)   // { text, low }: the agent's summary of the last detection

  const clearRoomReview = useCallback((reason = 'Cancelled') => {
    floorPendingRef.current = null
    setRoomPreview(null)
    setInferredCorePreview(null)
    setThread(t => t.map(x => x.question?.kind === 'floor_room' && !x.answered ? { ...x, answered: reason } : x))
  }, [])

  const markFloorOutdated = useCallback(() => {
    clearRoomReview('Drawing changed — update detection')
    setFloorResultNote({ text: 'Drawing changed — update detection to include your edits. Your changes are preserved.', low: true, outdated: true })
  }, [clearRoomReview])

  // Room previews never enter the manifest, saved drawing, or export.
  const showRoom = (pending, question) => {
    const room = pending.result.room_review.context.rooms.find(r => r.id === question.room_id)
    if (!room) {
      const repair = pending.result.room_review.context.repairs?.find(r => r.id === question.room_id)
      const lines = (repair?.preview_lines || []).map(line => line.map(([x,y]) => [x * pending.scale,y * pending.scale]))
      setRoomPreview(null)
      setInferredCorePreview(lines.length ? lines : null)
      if (lines.length) canvasRef.current?.zoomToRoom?.(lines)
      return
    }
    const polygons = room.geometry.type === 'MultiPolygon' ? room.geometry.coordinates : [room.geometry.coordinates]
    const rings = polygons.flatMap(polygon => polygon.map(ring => ring.map(([x, y]) => [x * pending.scale, y * pending.scale])))
    setRoomPreview(rings)
    setInferredCorePreview((room.inferred_boundary || []).map(line => line.map(([x,y]) => [x * pending.scale,y * pending.scale])))
    setActiveView('classified')
    canvasRef.current?.zoomToRoom?.(rings)
  }
  const askRoom = pending => {
    const q = pending.result.questions.find(q => !pending.answers[q.room_id])
    if (!q) return
    showRoom(pending, q)
    setThread(t => [...t, { id: `${pending.revisionId}-${q.room_id}`, role: 'agent',
      text: `${q.room_id}: ${q.reason}${q.repair_required ? '' : ' Choose the whole highlighted region. Cancel if its boundary needs editing.'}`,
      question: { id: q.room_id, kind: 'floor_room', options: q.repair_required ? ['Discard proposal', 'Cancel'] : q.overlap_review ? ['Core', 'Floor area', 'Cancel'] : q.floor_only ? ['Floor area', 'Exclude', 'Cancel'] : ['Core', 'Floor area', 'Exclude', 'Cancel'] } }])
    setAgent({ view: 'idle' }); setSidebarTab('agent')
  }
  floorShowRef.current = item => {
    const p = floorPendingRef.current
    if (p && item.id === `${p.revisionId}-${item.question.id}`) showRoom(p, { room_id: item.question.id })
  }
  floorAnswerRef.current = async (item, choice) => {
    const p = floorPendingRef.current
    if (!p || p.resolving || item.id !== `${p.revisionId}-${item.question.id}`) return
    if (choice === 'Cancel') { clearRoomReview(); return }
    const tag = { 'Discard proposal': 'floor', Core: 'core', 'Floor area': 'floor', Exclude: 'exclusion' }[choice]
    if (!tag || (p.answers[item.question.id] && p.result.questions.some(q => !p.answers[q.room_id]))) return
    setError(null)
    const current = () => p === floorPendingRef.current && p.run === runRef.current &&
      floorResultIsCurrent(p.result, p.revisionId, p.snapshot, floorSnapshotRef.current, p.scale, appliedScaleRef.current)
    if (!current()) { markFloorOutdated(); return }
    p.answers[item.question.id] = tag
    if (p.result.questions.some(q => !p.answers[q.room_id])) {
      setThread(t => t.map(x => x.id === item.id ? { ...x, answered: choice } : x))
      askRoom(p); return
    }
    p.resolving = true
    setIsDetectingFloor(true)
    try {
      const result = await resolveFloor(sessionId, p.result.floor_job_id, p.answers, p.result.review_token, p.revisionId)
      if (p !== floorPendingRef.current || p.run !== runRef.current) return
      if (!current()) { markFloorOutdated(); return }
      if (result.revision_id !== p.revisionId) throw new Error('Stale room result. Run detection again.')
      const found = floorEntities(result, p.scale, Date.now())
      if (!found.length) throw new Error('No usable floor remains. Choose another tag or cancel.')
      clearRoomReview(choice)
      const cur = editRef.current
      pushEdit({ ...cur, userPolylines: [...cur.userPolylines.filter(poly => !isFloorEntity(poly)), ...found] })
      setVisibleLayers(prev => new Set([...prev, ...found.map(f => f.layer)]))
      setFloorResultNote(floorNote(result))
      setThread(t => [...t, { id: `floor-resolved-${Date.now()}`, role: 'agent', text: 'Your room decisions were applied. You can undo the floor update.' }])
    } catch (err) {
      if (current()) setError(err.message) // keep the final question available to correct or retry
    } finally { p.resolving = false; setIsDetectingFloor(false) }
  }
  useEffect(() => {
    const p = floorPendingRef.current
    if (p && (p.run !== runRef.current || p.snapshot !== floorSnapshotRef.current || p.scale !== appliedScaleRef.current))
      markFloorOutdated()
  })

  // The floor and core come from the floor model (through the agent service), drawn from the plan as the user sees it now.
  // One floor per plan: the earlier one, hand-filled or detected, is replaced as one undoable edit.
  const handleDetectFloor = useCallback(async () => {
    if (!classifiedManifest || isDetectingFloor) return
    clearRoomReview()
    const run = runRef.current
    const startedAt = Date.now()
    const floorMsg = `floor-${startedAt}`
    const endFloorMsg = patch => setThread(t => t.map(x => x.id === floorMsg ? { ...x, live: false, ...patch } : x))
    setIsDetectingFloor(true); setError(null); setFloorStep(''); setFloorResultNote(null)
    if (isStaff) {   // staff watch the floor agent work in the Agent tab, like the classification review
      setThread(t => [...t, { id: floorMsg, role: 'agent', text: 'Finding the floor and the cores…', steps: [], live: true, startedAt }])
      setAgent({ view: 'running', stage: 'floor', steps: [], startedAt })
      setSidebarTab('agent')
    }
    const snapshot = floorSnapshotRef.current
    try {
      const submittedScale = appliedScaleRef.current
      const revisionId = crypto.randomUUID()
      const input = floorInput(classifiedManifest, uploadData.cleaned_manifest, submittedScale, revisionId)
      const admission = new AbortController()
      floorAdmissionRef.current = admission
      const { job_id } = await detectFloorRequest(sessionId, input, {
        signal: admission.signal,
        isCurrent: () => run === runRef.current && snapshot === floorSnapshotRef.current && submittedScale === appliedScaleRef.current,
      })
      const result = await pollJobStatus(job_id, {
        label: 'Floor detection', timeoutMs: AGENT_POLL_TIMEOUT_MS,
        onProgress: meta => {
          const last = (meta.steps ?? []).filter(st => st.kind === 'action').at(-1)
          if (last) setFloorStep(last.text)
          if (isStaff) {
            setAgent(prev => prev.view === 'running' && prev.stage === 'floor' ? { ...prev, steps: meta.steps ?? prev.steps } : prev)
            setThread(t => t.map(x => x.id === floorMsg ? { ...x, steps: meta.steps ?? x.steps } : x))
          }
        },
      })
      if (run !== runRef.current) return   // the file or classification changed meanwhile
      if (!floorResultIsCurrent(result, revisionId, snapshot, floorSnapshotRef.current, submittedScale, appliedScaleRef.current)) {
        markFloorOutdated()
        endFloorMsg({ text: 'Drawing changed — update detection to include your edits.' })
        setAgent({ view: 'idle' })
        return
      }
      if (result.needs_room_decisions) {
        const pending = { result, run, snapshot, scale: submittedScale, revisionId, answers: {} }
        floorPendingRef.current = pending
        endFloorMsg({ text: `${result.questions.length} rooms need your decision. The existing floor is unchanged.` })
        askRoom(pending)
        return
      }
      const found = floorEntities(result, submittedScale, Date.now())
      if (!found.length) throw new Error('The floor model found no floor in this plan.')
      const cur = editRef.current
      pushEdit({ ...cur, userPolylines: [...cur.userPolylines.filter(p => !isFloorEntity(p)), ...found] })
      setVisibleLayers(prev => new Set([...prev, ...found.map(f => f.layer)]))
      const note = floorNote(result)
      if (!isStaff) setFloorResultNote(note)
      if (isStaff) {
        const r = result.refinement ?? {}
        endFloorMsg({ text: note.text, steps: agentRef.current.steps ?? [], seconds: r.agent?.seconds ?? (Date.now() - startedAt) / 1000, cost: r.cost_usd, turns: r.agent?.turns })
        setAgent({ view: 'idle' })
      }
    } catch (err) {
      if (run !== runRef.current) return
      if (snapshot !== floorSnapshotRef.current) {
        markFloorOutdated()
        endFloorMsg({ text: 'Drawing changed — update detection to include your edits.' })
        setAgent({ view: 'idle' })
        return
      }
      const message = `Floor detection failed. ${err.message}`
      setError(null)
      setFloorResultNote({ text: message, low: true, failed: true })
      if (isStaff) { endFloorMsg({ text: message }); setAgent({ view: 'failed', error: message, stage: 'floor' }) }
    } finally { setIsDetectingFloor(false) }
  }, [classifiedManifest, uploadData, isDetectingFloor, isStaff, sessionId, pushEdit, say, clearRoomReview, markFloorOutdated])
  detectFloorRef.current = handleDetectFloor

  const handlePolylineVertexEdit = useCallback(async (handle, newPoints, newBulge) => {
    if (coreEditPendingRef.current) return
    const cur = editRef.current

    // Helper: apply newPoints (and optional bulge) to an entity
    const applyPts = (p) => {
      if (p.type === 'LINE') return applyLineVertexEdit(p, newPoints, newBulge)
      const result = { ...p, points: newPoints }
      if (newBulge !== undefined) {
        // Clear stale bulge when point count changed (insert/delete vertex), otherwise apply as-is
        if (newBulge.length !== newPoints.length - 1 && newBulge.length !== newPoints.length) {
          result.bulge = []
        } else {
          result.bulge = newBulge
        }
      }
      return result
    }

    // User-drawn entity (userPolylines)  —  match by explicit handle or generated user-{i}
    const userIdx = cur.userPolylines.findIndex((p, i) =>
      (p.handle != null ? p.handle === handle : `user-${i}` === handle)
    )
    if (userIdx !== -1) {
      const edited = applyPts(cur.userPolylines[userIdx])
      if (edited.layer === 'Core Q-Line' && cur.userPolylines.some(p => p.layer === 'Flood')) {
        const run = runRef.current
        const revisionId = crypto.randomUUID()
        const ring = p => ({ exterior: tessellatePolyline(p.points, p.bulge ?? p.bulges ?? [], true), holes: p.holes ?? [] })
        const cores = cur.userPolylines.filter(p => p.layer === 'Core Q-Line')
        coreEditPendingRef.current = true
        try {
          const result = await previewFloorEdit(sessionId, {
            action: 'replace_core', revision_id: revisionId, points: newPoints.slice(0, 2),
            floor: cur.userPolylines.filter(p => p.layer === 'Flood').map(ring),
            core: cores.map(ring), new_core: cores.map(p => ring(p === cur.userPolylines[userIdx] ? edited : p)),
          })
          if (run !== runRef.current || cur !== editRef.current) return
          if (result.revision_id !== revisionId) throw new Error('The drawing changed. Please retry the core edit.')
          const floors = floorEntities({ floor: result.floor }, 1, Date.now())
          pushEdit({ ...cur, userPolylines: [
            ...cur.userPolylines.filter(p => p.layer !== 'Flood').map(p => p === cur.userPolylines[userIdx] ? edited : p), ...floors,
          ] })
        } catch (err) {
          if (run === runRef.current && cur === editRef.current) setError(`Core edit failed. ${err.message}`)
        } finally {
          coreEditPendingRef.current = false
        }
        return
      }
      pushEdit({ ...cur, userPolylines: cur.userPolylines.map((p, i) => i === userIdx ? edited : p) })
      return
    }

    // Preprocess-derived entity (preprocessedMods)
    const modIdx = (cur.preprocessedMods ?? []).findIndex(p => p.handle === handle)
    if (modIdx !== -1) {
      pushEdit({ ...cur, preprocessedMods: cur.preprocessedMods.map((p, i) => i === modIdx ? applyPts(p) : p) })
      return
    }

    // DXF entity  —  store in entityOverrides
    const base = cur.entityOverrides[handle] ?? manifestByHandleRef.current.get(handle)
    if (!base) return
    pushEdit({ ...cur, entityOverrides: { ...cur.entityOverrides, [handle]: applyPts(stripProtection(base)) } })
  }, [pushEdit, sessionId])

  const handleSymbolPlace = useCallback((wx, wy) => {
    const sym = SYMBOLS.find(s => s.id === activeSymbol)
    if (!sym || !classifiedManifest) return
    const bounds = computeBounds(classifiedManifest)
    const drawingSize = Math.max(
      (bounds.maxX - bounds.minX) || 100,
      (bounds.maxY - bounds.minY) || 100,
    )
    const scale = drawingSize / 25 / 100  // symbol 100-unit box -> 1/25 of drawing
    const ts = Date.now()
    const newEntities = instantiateSymbol(sym, wx, wy, scale).map((e, i) => ({
      ...e,
      handle: `symbol-${ts}-${i}`,
      layer: 'Interior Design',
      isUserDrawn: true,
    }))
    pushEdit({
      ...editRef.current,
      userPolylines: [...editRef.current.userPolylines, ...newEntities],
    })
  }, [activeSymbol, classifiedManifest, pushEdit])

  const handleGenerateDoorQLines = useCallback(() => {
    if (!classifiedManifest) return
    const qlines = generateDoorQLines(classifiedManifest)
    if (!qlines.length) {
      const layers = [...new Set(classifiedManifest.map(e => e.layer).filter(Boolean))].join(', ')
      setError(`No door openings detected. Layers in manifest: ${layers || '(none)'}`)
      setTimeout(() => setError(null), 8000)
      return
    }
    pushEdit({
      ...editRef.current,
      userPolylines: [...editRef.current.userPolylines, ...qlines],
    })
    setVisibleLayers(prev => new Set([...prev, 'Door Q-Line']))
  }, [classifiedManifest, pushEdit])

  const handleFocusLayer = useCallback((layerKey) => {
    setFocusedLayer(prev => prev === layerKey ? null : layerKey)
  }, [])

  const handleScaleMeasure = useCallback(([p1, p2]) => {
    setScaleDialog({ p1, p2 })
    setActiveTool('pan')
  }, [])

  const handleFindSimilar = useCallback(() => {
    if (!selectedHandles.size) return
    const targets = [...selectedHandles]
      .map(h => manifestByHandleRef.current.get(h))
      .filter(Boolean)
    if (!targets.length) return
    const pool = manifest.filter(e => !selectedHandles.has(e.handle))

    // Pass 1 — Exact hash: true CAD copies (copy/paste), ~0.01% rounding tolerance
    const exact = findExactMatches(targets, pool)
    if (exact.size > 0) { setSelectedHandles(new Set([...selectedHandles, ...exact])); return }

    // Pass 2 — Near-exact: same shape with minor numeric noise (1%)
    const near = findSimilarEntities(targets, pool, 0.01)
    if (near.size > 0) { setSelectedHandles(new Set([...selectedHandles, ...near])); return }

    // Pass 3 — Similar: same element type with slight variation (4%)
    const similar = findSimilarEntities(targets, pool, 0.04)
    if (similar.size > 0) { setSelectedHandles(new Set([...selectedHandles, ...similar])); return }
  }, [selectedHandles, manifest])

  const handleMoveToLayer = useCallback((targetLayer) => {
    if (!selectedHandles.size) return
    const cur = editRef.current
    const newOverrides        = { ...cur.entityOverrides }
    const newUserPolylines    = cur.userPolylines.map(p => ({ ...p }))
    const newPreprocessedMods = (cur.preprocessedMods ?? []).map(p => ({ ...p }))
    const newSublayerKeys     = new Set()
    let changed = false

    for (const h of selectedHandles) {
      // 1. userPolylines — drawn entities (drawn-poly-*, drawn-flood-*) and copies in classified view
      const upIdx = cur.userPolylines.findIndex((p, i) => (p.handle ?? `user-${i}`) === h)
      if (upIdx !== -1) {
        newUserPolylines[upIdx] = { ...newUserPolylines[upIdx], layer: targetLayer }
        changed = true
        continue
      }
      // 2. preprocessedMods — copies/explode in preprocess/original view
      const pmIdx = (cur.preprocessedMods ?? []).findIndex(p => p.handle === h)
      if (pmIdx !== -1) {
        newPreprocessedMods[pmIdx] = { ...newPreprocessedMods[pmIdx], layer: targetLayer }
        changed = true
        continue
      }
      // 3. DXF manifest entity — store override
      const base = cur.entityOverrides[h] ?? manifestByHandleRef.current.get(h)
      if (base) {
        newOverrides[h] = { ...base, layer: targetLayer }
        changed = true
        if (base.original_layer_name) newSublayerKeys.add(reclassifiedLayerName(base.original_layer_name, targetLayer))
      }
    }

    if (!changed) return
    const newClassOverrides = { ...cur.handleClassOverrides }
    const newProtectedLayers = { ...cur.protectedLayers }
    for (const h of selectedHandles) {
      newClassOverrides[h] = targetLayer
      delete newProtectedLayers[h]
    }
    pushEdit({ ...cur, entityOverrides: newOverrides, handleClassOverrides: newClassOverrides, userPolylines: newUserPolylines, preprocessedMods: newPreprocessedMods, protectedLayers: newProtectedLayers })
    setVisibleLayers(prev => new Set([...prev, targetLayer, ...newSublayerKeys]))
    setSelectedHandles(new Set())
  }, [selectedHandles, pushEdit])

  const handleToggleApprove = useCallback((cls) => {
    setApprovedLayers(prev => {
      const next = new Set(prev)
      next.has(cls) ? next.delete(cls) : next.add(cls)
      return next
    })
  }, [])

  const handleApproveMultiple = useCallback((classes, shouldApprove) => {
    setApprovedLayers(prev => {
      const next = new Set(prev)
      for (const cls of classes) shouldApprove ? next.add(cls) : next.delete(cls)
      return next
    })
  }, [])

  const handleIsolateMultiple = useCallback((classes, triggeredBy) => {
    setSoloLayers(prev => {
      const next = new Set(prev)
      const isIsolated = prev.has(triggeredBy)
      for (const cls of classes) isIsolated ? next.delete(cls) : next.add(cls)
      return next
    })
  }, [])

  const layerStats = useMemo(() => getOriginalLayerStats(classifiedManifest), [classifiedManifest])

  const floodNetArea = useMemo(() => computeNetArea(classifiedManifest ?? []), [classifiedManifest])

  const classifyWarnings = useMemo(() => {
    if (!classifiedManifest) return []
    const warns = []
    const colPolys = classifiedManifest.filter(e => e.layer === 'Columns' && e.type === 'LWPOLYLINE')
    if (colPolys.length > 0 && colPolys.some(e => !e.closed))
      warns.push('!  Some column shapes are not closed. Use the Draw tool to fix them.')
    if (!classifiedManifest.some(e => e.layer === 'Main Entrance Q-Line'))
      warns.push('!  No Main Entrance Q-Line found in the drawing.')
    if (!classifiedManifest.some(e => e.layer === 'Door Q-Line'))
      warns.push('!  No Door Q-Line found in the drawing.')
    return warns
  }, [classifiedManifest])

  const doExport = useCallback(() => {
    setIsFinishing(true)
    try {
      const fname = (uploadData?.filename?.replace(/\.\w+$/, '') || 'classified') + '_classified.dxf'
      const exported = downloadDxf(classifiedManifest, fname)
      downloadLineage(buildLineageReport(uploadData?.document_id ?? sessionId, uploadData?.lineage, edit, classifiedManifest), exported, fname.replace(/\.dxf$/, '_mapping.json'))

      // Save feedback first, then download the audit JSON (so user_label is filled in)
      const triggerAuditDownload = () => {
        downloadAudit(sessionId)
          .then(blob => {
            if (!blob) return
            const base = uploadData?.filename?.replace(/\.\w+$/, '') || 'unknown'
            const a = document.createElement('a')
            a.href = URL.createObjectURL(blob)
            a.download = `${base}_audit.json`
            a.click()
            URL.revokeObjectURL(a.href)
          })
          .catch(() => {})
      }

      if (classifyData) {
        const classMap = {}
        for (const e of classifiedManifest) {
          if (!e.isUserDrawn && e.handle && e.layer) {
            if (!classMap[e.layer]) classMap[e.layer] = []
            classMap[e.layer].push(e.handle)
          }
        }
        const classAssignments = Object.entries(classMap).map(([cls, handles]) => ({
          predicted_class: cls, handles,
        }))
        const transferredCount = (edit.preprocessedEdits || []).filter(e => e.isTransferred).length
        saveFeedback({
          session_id:              sessionId,
          class_assignments:       classAssignments,
          deleted_handles:         edit.deletedHandles || [],
          handle_class_overrides:  edit.handleClassOverrides || {},
          layer_remaps:            edit.layerRemaps || {},
          user_drawn_count:        (edit.userPolylines || []).length,
          transferred_count:       transferredCount,
        })
          .then(() => triggerAuditDownload())
          .catch(err => { console.warn('Feedback save failed (non-critical):', err); triggerAuditDownload() })
      } else {
        triggerAuditDownload()
      }
    } catch (e) { setError(e.message) }
    finally { setIsFinishing(false); setShowWarningExport(false) }
  }, [classifiedManifest, uploadData, classifyData, edit, sessionId])

  const handleFinish = useCallback(async () => {
    if (!classifiedManifest) return
    setIsRevalidating(true)
    try {
      // Build class assignments from final classified manifest
      // Untagged / missing layer -> '0'
      const classMap = {}
      for (const e of classifiedManifest) {
        if (e.isUserDrawn || e.handle == null) continue
        const cls = (!e.layer || e.layer === 'Untagged') ? '0' : e.layer
        if (!classMap[cls]) classMap[cls] = []
        classMap[cls].push(e.handle)
      }
      const class_assignments = Object.entries(classMap).map(
        ([predicted_class, handles]) => ({ predicted_class, handles })
      )
      const { job_id: revalJobId } = await rerunLayerClassifier(sessionId, class_assignments)
      const { results } = await pollJobStatus(revalJobId, { label: 'Revalidation' })
      const counts = Object.fromEntries(Object.entries(classMap).map(([k, v]) => [k, v.length]))
      setRevalidateData({ results, counts })
      setShowPreExport(true)
    } catch (e) {
      setError(e.message)
    } finally {
      setIsRevalidating(false)
    }
  }, [classifiedManifest, setError, sessionId])

  const handleChatHighlight = useCallback((id) => {
    if (hlTimerRef.current) clearTimeout(hlTimerRef.current)
    setHighlightEl(id)
    hlTimerRef.current = setTimeout(() => setHighlightEl(null), 6000)
  }, [])

const hl = (id) => highlightEl === id ? 'hl-pulse' : ''
  const isScaledToCm = detectedUnits !== null || manuallyScaled || scaleFactor === 1

  const selectedLayers = useMemo(() => {
    if (!selectedHandles.size || !manifest) return new Set()
    const layers = new Set()
    for (const h of selectedHandles) {
      const e = manifestByHandle.get(h)
      if (e?.layer) layers.add(e.layer)
    }
    return layers
  }, [selectedHandles, manifest, manifestByHandle])

  const handleIsolateLayer = useCallback((layerKey) => {
    setSoloLayers(prev => {
      const next = new Set(prev)
      next.has(layerKey) ? next.delete(layerKey) : next.add(layerKey)
      return next
    })
  }, [])

  useEffect(() => {
    const onKey = (e) => {
      const action = getCanvasShortcutAction(e, {
        selectedCount: selectedHandles.size,
        copyBufferCount: copyBufferRef.current.length,
        pendingMove,
        pendingTransform,
      })
      if (!action) return

      e.preventDefault()
      if (action === 'undo') { undo(); return }
      if (action === 'redo') { redo(); return }
      if (action === 'erase') { handleErase(); return }
      if (action === 'startMove') { startMove(); return }
      if (action === 'startCopy') {
        copyBufferRef.current = [...selectedHandles].map(h => resolveEnt(h)).filter(Boolean)
        startCopy()
        return
      }
      if (action === 'startPaste') {
        setPendingMove({ mode: 'paste', step: 'base', handles: new Set(), pasteEnts: copyBufferRef.current })
        return
      }
      if (action === 'explode') { handleExplode(); return }
      if (action === 'join') { handleJoin(); return }
      if (action === 'startRotate') { startRotate(); return }
      if (action === 'cycleMirror') { handleCycleMirror(); return }
      if (pendingMove) { setPendingMove(null); return }
      if (pendingTransform) { setPendingTransform(null); return }
      setSelectedHandles(new Set())
      setFocusedLayer(null)
      setSoloLayers(new Set())
      setShiftSelectedLayers(new Set())
      symbolDropdown.close()
      canvasRef.current?.cancelTool()
      if (activeTool !== 'draw') setActiveTool('pan')
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [undo, redo, handleErase, selectedHandles, activeTool, pendingMove, pendingTransform, startMove, startCopy, startRotate, handleCycleMirror, handleExplode, handleJoin, resolveEnt, symbolDropdown.close])

  return (
    <div className="app">
      <header className="header">
        <div className="header-left">
          <QbiqLogo width={100} />
        </div>
        <div className="header-center">
          <span className="page-title">AutoClassifier</span>
          <SessionStats />
          <PerformanceMonitor />
        </div>
        <div className="header-right">
          {uploadData && <span className="header-filename">{uploadData.filename}</span>}
          <label className={`btn btn-ghost ${hl('upload')}`}>
            {isUploading ? <><span className="spin" /> Uploading...</> : uploadData ? 'Change File' : 'Upload File'}
            <input type="file" accept=".dxf" style={{ display:'none' }}
              onChange={e => handleUpload(e.target.files[0])} disabled={isUploading || isClassifying || isReopening} />
          </label>
          <button
            className={`btn btn-ghost ${showRecent ? 'active' : ''}`}
            onClick={() => setShowRecent(v => !v)}
            disabled={isUploading || isClassifying || isReopening}
            title="Recent sessions"
          >
            Recent
          </button>
          <button
            className="btn btn-ghost"
            onClick={async () => {
              if (await flushSaveRef.current?.() !== false) logout({ logoutParams: { returnTo: `${window.location.origin}/login-sso` } })
            }}
            title="Log out"
          >
            Log out
          </button>
        </div>
      </header>

      <div className="body">
        <CollapsiblePanel storageKey="sidebarCollapsed" width={(isStaff || floorPendingRef.current) && sidebarTab === 'agent' ? 420 : 256}>
          <div className="sidebar-shell">
          {(isStaff || floorPendingRef.current) && (
            <div className="sidebar-mode-tabs" role="tablist">
              <button role="tab" aria-selected={sidebarTab === 'classes'} className={sidebarTab === 'classes' ? 'on' : ''}
                onClick={() => { setSidebarTab('classes'); setAgentPreview(null) }}>Classes</button>
              <button role="tab" aria-selected={sidebarTab === 'agent'} className={sidebarTab === 'agent' ? 'on' : ''}
                onClick={() => setSidebarTab('agent')}>
                Agent{agent.view === 'running' && <span className="spin" />}
              </button>
            </div>
          )}
          {(isStaff || floorPendingRef.current) && sidebarTab === 'agent' ? (
            <AgentChat thread={thread} agent={agent} handlers={agentHandlers}
              onReview={() => handleArchitect()}
              classified={!!classifyData && !isClassifying && !floorPendingRef.current} leftovers={leftoverHandles.length}
              showLeftovers={showLeftovers} onToggleLeftovers={() => setShowLeftovers(v => !v)} />
          ) : (

          <Sidebar
            activeView={activeView}
            setActiveView={handleTabChange}
            uploadData={uploadData}
            classifyData={classifyData ? { manifest: classifiedManifest } : null}
            visibleLayers={visibleLayers}
            setVisibleLayers={setVisibleLayers}
            focusedLayer={focusedLayer}
            onFocusLayer={handleFocusLayer}
            activeLayer={activeLayer}
            setActiveLayer={setActiveLayer}
            activeTool={scaleLocked && activeTool === 'scale' ? 'pan' : activeTool}
            layerStats={layerStats}
            naturalDistribution={naturalDistribution}
            approvedLayers={approvedLayers}
            onToggleApprove={handleToggleApprove}
            onApproveMultiple={handleApproveMultiple}
            onIsolateMultiple={handleIsolateMultiple}
            selectedHandles={selectedHandles}
            onMoveToLayer={activeView === 'classified' ? handleMoveToLayer : null}
            selectedLayers={selectedLayers}
            selectionDetails={selectionDetails}
            soloLayers={soloLayers}
            onIsolateLayer={handleIsolateLayer}
            onFinish={handleFinish}
            isFinishing={isFinishing || isRevalidating}
            onClassify={handleClassify}
            isClassifying={isClassifying}
            agentBusy={agent.view === 'running' && agent.stage !== 'preclean'}
            planSize={planSize}
            scaleLocked={scaleLocked}
            onOpenUnits={() => { if (!scaleLocked) setUnitsDialog(true) }}
            autoReviewOn={autoReviewOn}
            onAutoReviewChange={isStaff ? handleAutoReviewChange : null}
            precleanLayers={precleanLayers}
            onSublayerClick={(sub, color) => {
              const isOpening = popup?.sublayer.name !== sub.name || popup?.sublayer.qbiqClass !== sub.qbiqClass
              setPopup(isOpening ? { sublayer: sub, groupColor: color } : null)
              setFocusedLayer(isOpening ? sub.name : null)
              setShiftSelectedLayers(new Set(isOpening ? [sub.name] : []))
            }}
            activePopupName={popup?.sublayer.name}
            onShowConfRemap={() => setShowConfRemap(true)}
            highlightEl={highlightEl}
            protectedLayers={edit.protectedLayers}
            shiftSelected={shiftSelectedLayers}
            setShiftSelected={setShiftSelectedLayers}
          />
          )}
          </div>
        </CollapsiblePanel>

        <div className="canvas-wrap"
          onDragOver={e => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => {
            e.preventDefault(); setDragOver(false)
            if (isUploading || isClassifying || isReopening) return
            const f = e.dataTransfer.files[0]; if (f) handleUpload(f)
          }}>


          {saveError && (
            <div className="error-toast" role="alert">
              <span>{saveError}</span>
              {!saveConflict && <button onClick={() => flushSaveRef.current?.()}>Retry save</button>}
              {saveConflict && !Number.isInteger(saveConflict.revision) && <button onClick={() => flushSaveRef.current?.()}>Check conflict</button>}
              {saveConflict && Number.isInteger(saveConflict.revision) && <button onClick={() => {
                if (window.confirm('Replace the saved drawing with your current local edits? Changes saved in the other tab will be replaced.')) {
                  canvasSaver.setRevision(saveConflict.revision)
                  flushSaveRef.current?.()
                }
              }}>Save my version</button>}
              {saveConflict && <button onClick={() => {
                if (window.confirm('Discard your unsaved local edits and reopen the latest saved drawing?')) handleReopen({ session_id: sessionId }, true)
              }}>Reopen saved version</button>}
            </div>
          )}
          {!saveError && error && (
            <div className="error-toast">
              <span>{error}</span>
              <button onClick={() => setError(null)}>x</button>
            </div>
          )}

          {activeView === 'classified' && floodNetArea > 0 && (
            <div className="flood-area-pill">
              Net Area:&nbsp;
              <strong>
                {scaleFactor
                  ? `${(floodNetArea * scaleFactor * scaleFactor / 10000).toFixed(1)} m²`
                  : `${floodNetArea.toFixed(1)} u²`}
              </strong>
            </div>
          )}
          {isDetectingFloor && (
            <div className="flood-area-pill">Detecting the floor{!isStaff && floorStep ? ` — ${floorStep}` : '...'}</div>
          )}
          {floorResultNote && !isDetectingFloor && (
            <div className={`flood-area-pill floor-note ${floorResultNote.low ? 'low' : ''}`} role={floorResultNote.failed ? 'alert' : 'status'}
              onClick={floorResultNote.failed || floorResultNote.outdated ? undefined : () => setFloorResultNote(null)}>
              {floorResultNote.text}
              {(floorResultNote.failed || floorResultNote.outdated) && <><button onClick={handleDetectFloor}>{floorResultNote.outdated ? 'Update detection' : 'Retry floor detection'}</button>
                <button aria-label="Dismiss floor notice" onClick={() => setFloorResultNote(null)}>×</button></>}
            </div>
          )}

          {!manifest && !pdfState && !isUploading && !isPdfLoading && (
            <div className={`drop-zone ${dragOver ? 'drag-active' : ''}`}>
              <QbiqLogo width={200} />
              <h2>Drop your floor plan here</h2>
              <p>Supports .dxf files</p>
              <label className="btn btn-primary" style={{ marginTop:16 }}>
                Browse Files
                <input type="file" accept=".dxf" style={{ display:'none' }}
                  onChange={e => handleUpload(e.target.files[0])} disabled={isReopening} />
              </label>
            </div>
          )}

          {isPdfLoading && !pdfState && (
            <div className="overlay">
              <div className="spin large" />
              <p>Reading PDF layers...</p>
            </div>
          )}

          {(isUploading || isClassifying || isReopening) && (
            <div className="overlay">
              <div className="spin large" />
              <p>{isReopening ? 'Reopening session...' : isUploading ? 'Processing file...'
                : agent.view === 'running' && agent.stage === 'preclean' ? 'Pre-cleaning, then classifying...' : 'Running AI classification...'}</p>
              {isClassifying && agent.view === 'running' && agent.stage === 'preclean' && (
                <ol className="overlay-steps" aria-live="polite">
                  {(agent.steps ?? []).slice(-6).map((st, i) => <li key={i}>{st.text}</li>)}
                </ol>
              )}
            </div>
          )}

          {showRecent && (
            <RecentSessionsPanel
              onClose={() => setShowRecent(false)}
              onReopen={handleReopen}
            />
          )}

          {(manifest || pdfState) && (
            <DxfCanvas
              ref={canvasRef}
              manifest={manifest}
              focusHandles={popupFocusHandles}
              roomPreview={roomPreview}
              inferredCorePreview={inferredCorePreview}
              fitKey={manifestFitKey}
              visibleLayers={visibleLayers}
              classColors={CLASS_COLORS}
              activeView={activeView}
              activeTool={scaleLocked && activeTool === 'scale' ? 'pan' : activeTool}
              selectedHandles={canvasSelection}
              selectedColorMap={popup?.colorMap ?? agentPreview ?? null}
              isolatedLayer={focusedLayer ?? ((activeTool === 'draw' || activeTool === 'shape') ? activeLayer : null)}
              activeLayer={activeLayer}
              onZoomChange={setZoom}
              onFenceComplete={handleFenceComplete}
              onClickSelect={handleClickSelect}
              onBoxSelectComplete={handleBoxSelectComplete}
              onMoveComplete={handleMoveComplete}
              onPolylineComplete={handlePolylineComplete}
              onExitDrawTool={() => setActiveTool('pan')}
              onPolylineVertexEdit={handlePolylineVertexEdit}
              onPolylineSplit={handleSplitVertex}
              onSymbolPlace={activeView === 'classified' ? handleSymbolPlace : undefined}
              stampSymbol={activeSymbol ? SYMBOLS.find(s => s.id === activeSymbol) : null}
              activeShape={activeShape}
              onShapeComplete={activeView === 'classified' ? handleShapeComplete : undefined}
              scaleFactor={scaleFactor}
              soloLayers={soloLayers}
              shiftSelectedLayers={shiftSelectedLayers}
              onScaleMeasure={handleScaleMeasure}
              snapEnabled={snapEnabled}
              isScaledToCm={isScaledToCm}
              pdfBg={pdfState ? { img: pdfState.img, worldW: pdfState.worldW, worldH: pdfState.worldH } : null}
              pickPointMode={!!pendingMove || !!pendingTransform}
              onPointPick={handlePointPick}
              movePreview={pendingMove?.step === 'dest' ? { entities: pendingMove.pasteEnts, base: [pendingMove.baseWX, pendingMove.baseWY] } : null}
              transformPreview={
                pendingTransform?.step === 'angle'
                  ? { mode: 'rotate', entities: pendingTransform.entities, cx: pendingTransform.cx, cy: pendingTransform.cy }
                  : null
              }
            />
          )}

          {/* PDF layer panel */}
          {pdfState && (
            <div className="pdf-layer-panel">
              <div className="pdf-layer-header">
                <span className="pdf-layer-title">
                  <IconFile style={{marginRight:5,verticalAlign:'middle'}} />
                  PDF Layers
                  {isPdfLoading && <span className="spin" style={{marginLeft:6,verticalAlign:'middle'}}/>}
                </span>
                <button className="icon-btn" onClick={handleRemovePdf} title="Remove PDF">
                  <IconClose />
                </button>
              </div>
              <div className="pdf-layer-list">
                {pdfState.layers.map(layer => (
                  <label key={layer.id} className="pdf-layer-row">
                    <input
                      type="checkbox"
                      checked={pdfState.layerVis.get(layer.id) !== false}
                      onChange={() => handlePdfLayerToggle(layer.id)}
                      disabled={isPdfLoading}
                    />
                    <span className="pdf-layer-name" title={layer.name}>{layer.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Active layer / solo pill */}
          {(focusedLayer || activeTool === 'draw' || soloLayers.size > 0) && (
            <div className="isolation-pill">
              {soloLayers.size > 0
                ? <><span>Isolated: <strong>{[...soloLayers].map(toDisplayName).join(', ')}</strong></span><button onClick={() => setSoloLayers(new Set())}>x</button></>
                : focusedLayer
                  ? <><span>Layer: <strong>{toDisplayName(focusedLayer)}</strong></span><button onClick={() => setFocusedLayer(null)}>x</button></>
                  : <span>Drawing on: <strong>{toDisplayName(activeLayer)}</strong></span>
              }
            </div>
          )}

          {/* Probability popup */}
          {popup && (
            <ProbPopup
              sublayer={popup.sublayer}
              groupColor={popup.groupColor}
              onClose={() => { setPopup(null); setFocusedLayer(null); setPendingRemap(null); setSelectedHandles(new Set()) }}
              onRemap={handleRemap}
              splitData={classifyData?.layer_split_data?.[popup.sublayer.name] ?? null}
              sublayerHandles={isolatePopupEntities(classifiedManifest, { sublayer: popup.sublayer }).map(e => e.handle)}
              existingOverrides={edit.handleClassOverrides}
              unitScale={appliedScaleRef.current}
              onGroupSelect={(handles, colorMap) => {
                setSelectedHandles(handles ? new Set(handles) : new Set())
                setPopup(p => p && { ...p, colorMap: colorMap ?? null, isolatedHandles: handles })
              }}
              onApplySplit={(origLayerName, assignments) => {
                setPopup(p => p && { ...p, colorMap: null })
                handleApplySplit(origLayerName, assignments)
              }}
            />
          )}

          {/* Confidence remap panel */}
          {showConfRemap && (
            <ConfRemapPanel
              onApply={handleConfidenceRemap}
              onClose={() => setShowConfRemap(false)}
            />
          )}

          {/* Scale dialog */}
          {scaleDialog && !scaleLocked && (
            <ScaleDialog
              p1={scaleDialog.p1}
              p2={scaleDialog.p2}
              isScaledToCm={isScaledToCm}
              onApply={handleApplyScale}
              onClose={() => {
                setScaleDialog(null)
                if (unitsReturnRef.current) { unitsReturnRef.current = false; setUnitsDialog(true) }
              }}
            />
          )}

          {/* Drawing units (AutoCAD UNITS + SCALE → Reference) */}
          {unitsDialog && uploadData && !scaleLocked && (
            <DrawingUnitsDialog cmPerUnit={appliedScaleRef.current} size={planSize} {...unitsHandlers} />
          )}

          {/* Selection bar */}
          {selectedHandles.size > 0 && (
            <div className="selection-bar">
              {pendingMove ? (
                <>
                  <span className="sel-pick-prompt">
                    {pendingMove.mode === 'move' ? 'Move' : 'Copy'}
                  </span>
                  <span className="sel-pick-step">
                    {pendingMove.step === 'base' ? 'Click base point' : 'Click destination'}
                  </span>
                  <button className="clear-sel-btn" onClick={() => setPendingMove(null)}>Cancel</button>
                </>
              ) : pendingTransform ? (
                <>
                  <span className="sel-pick-prompt">Rotate</span>
                  <span className="sel-pick-step">
                    {pendingTransform.step === 'center' ? 'Click rotation center' : 'Click to set angle  —  Shift: snap 45°'}
                  </span>
                  <button className="clear-sel-btn" onClick={() => setPendingTransform(null)}>Cancel</button>
                </>
              ) : (
                <>
                  <span>{selectedHandles.size} selected</span>
                  <span className="sel-bar-div" />
                  <button className="sel-action-btn" onClick={startMove} title="Move — M">
                    <IconMove />
                    Move
                  </button>
                  <button className="sel-action-btn" onClick={startCopy} title="Copy — C / V">
                    <IconCopy />
                    Copy
                  </button>
                  <button className="sel-action-btn" onClick={handleJoin} title="Join selected lines/polylines end-to-end into one polyline" disabled={selectedHandles.size < 2}>
                    <IconJoin />
                    Join
                  </button>
                  <button className="sel-action-btn" onClick={handleExplode} title="Explode selected polylines into individual line segments">
                    <IconExplode />
                    Explode
                  </button>
                  <button className="sel-action-btn" onClick={startRotate} title="Rotate — R">
                    <IconRotate />
                    Rotate
                  </button>
                  <span className="sel-bar-div" />
                  <button className="sel-action-btn" onClick={handleFindSimilar} title="Find all geometries with the same shape and size">
                    <IconSearch />
                    Find Similar
                  </button>
                  {activeView === 'preprocessed' && (
                    <div className="sel-layer-wrap" ref={setLayerDropdown.ref}>
                      <button className="sel-action-btn" onClick={setLayerDropdown.toggle}
                        title="Assign selection to a protected layer — excluded from AI classification">
                        <IconLayers />
                        Set Layer
                        <IconCaretDown />
                      </button>
                      {setLayerDropdown.open && (
                        <div className="sel-layer-menu">
                          {['Interior Design', 'Furniture'].map(cls => (
                            <button key={cls} className="sel-layer-item" onClick={() => handleProtectAssign(cls)}>
                              <span className="sel-layer-swatch" style={{ background: CLASS_COLORS[cls] }} />
                              {cls}
                            </button>
                          ))}
                          {[...selectedHandles].some(h => edit.protectedLayers?.[h]) && (
                            <button className="sel-layer-item sel-layer-clear" onClick={() => handleProtectAssign(null)}>
                              Clear assignment
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {activeView === 'original' && (
                    <button className="sel-action-btn" onClick={handleTransfer}>
                      <IconChevronRight />
                      Transfer to Preprocessed
                    </button>
                  )}
                  <button className="sel-action-btn danger" onClick={handleErase} title="Erase (Backspace)">
                    <IconTrash />
                    Erase
                  </button>
                  <button className="clear-sel-btn" onClick={() => setSelectedHandles(new Set())}>Clear</button>
                </>
              )}
            </div>
          )}

          {manifest && (
            <div className="toolbar">
              <div className="tool-group">
                {/* Default / Pan+Select */}
                <button
                  className={`tool-btn ${activeTool === 'pan' ? 'active' : ''} ${hl('pan')}`}
                  onClick={() => { setActiveTool('pan'); canvasRef.current?.cancelTool() }}
                  title="Default — select / pan">
                  <IconCursor />
                </button>

                {/* Scale tool */}
                <div className="tool-btn-wrap">
                  <button
                    className={`tool-btn ${activeTool === 'scale' ? 'active' : ''} ${hl('scale')}`}
                    onClick={() => { setActiveTool(t => t === 'scale' ? 'pan' : 'scale'); canvasRef.current?.cancelTool() }}
                    disabled={scaleLocked}
                    title={scaleLocked ? 'Scale is locked while processing' : 'Scale Tool  —  click 2 points to calibrate real-world distance (cm)'}>
                    <IconRuler />
                  </button>
                  {isScaledToCm && (
                    <span className="tool-unit-badge">cm</span>
                  )}
                </div>

                {/* Draw polyline */}
                <button
                  className={`tool-btn ${activeTool === 'draw' ? 'active' : ''} ${hl('draw')}`}
                  onClick={() => { setActiveTool(t => t === 'draw' ? 'pan' : 'draw'); canvasRef.current?.cancelTool() }}
                  title="Draw Polyline  —  click to add points, double-click to finish"
                  disabled={activeView !== 'classified'}>
                  <IconPencil />
                </button>

                {/* Shape tools  —  dropdown */}
                <div className="symbol-dropdown-wrap" ref={shapeDropdown.ref}>
                  <button
                    className={`tool-btn symbol-dropdown-btn ${activeTool === 'shape' ? 'active' : ''}`}
                    onClick={shapeDropdown.toggle}
                    disabled={activeView !== 'classified'}
                    title="Shape tools  —  draw rectangles, circles, arcs">
                    <IconShapes />
                    <IconCaretDown />
                  </button>
                  {shapeDropdown.open && (
                    <div className="symbol-dropdown-menu">
                      {[
                        { id: 'rectangle', label: 'Rectangle', icon: (
                          <IconRectangle />
                        )},
                        { id: 'circle', label: 'Circle', icon: (
                          <IconCircle />
                        )},
                        { id: 'arc', label: 'Arc', icon: (
                          <IconArc />
                        )},
                      ].map(({ id, label, icon }) => (
                        <button
                          key={id}
                          className={`sym-menu-item ${activeTool === 'shape' && activeShape === id ? 'active' : ''}`}
                          onClick={() => {
                            if (activeTool === 'shape' && activeShape === id) {
                              setActiveTool('pan'); setActiveShape(null); canvasRef.current?.cancelTool()
                            } else {
                              setActiveTool('shape'); setActiveShape(id)
                              canvasRef.current?.cancelTool()
                            }
                            shapeDropdown.close()
                          }}>
                          {icon}
                          <span>{label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Detect floor: the floor and core from the floor model */}
                <button
                  className={`tool-btn ${isDetectingFloor ? 'active' : ''}`}
                  onClick={handleDetectFloor}
                  title="Detect floor  —  find the floor and complete core blocks (replaces the current floor)"
                  disabled={activeView !== 'classified' || isDetectingFloor}>
                  <IconPaintBucket />
                </button>
                {/* Edit floor: drag the floor's vertices without selecting it, double-click an edge to add one */}
                <button
                  className={`tool-btn ${activeTool === 'flood' ? 'active' : ''}`}
                  onClick={() => { setActiveTool(t => t === 'flood' ? 'pan' : 'flood'); canvasRef.current?.cancelTool() }}
                  title="Edit floor  —  drag a vertex, double-click an edge to add one"
                  disabled={activeView !== 'classified'}>
                  <IconTolerance />
                </button>

                {/* Auto Door Q-Lines */}
                <button
                  className="tool-btn"
                  onClick={handleGenerateDoorQLines}
                  disabled={activeView !== 'classified'}
                  title="Auto Door Q-Lines  —  detect door openings and draw Q-lines">
                  <IconDoorQLines />
                </button>

                {/* Interior design symbols  —  dropdown */}
                <div className="symbol-dropdown-wrap" ref={symbolDropdown.ref}>
                  <button
                    className={`tool-btn symbol-dropdown-btn ${activeTool === 'symbolStamp' ? 'active' : ''}`}
                    onClick={symbolDropdown.toggle}
                    disabled={activeView !== 'classified'}
                    title="Symbol stamps  —  place interior design symbols">
                    <IconGrid />
                    <IconCaretDown />
                  </button>

                  {symbolDropdown.open && (
                    <div className="symbol-dropdown-menu">
                      {SYMBOLS.map(sym => (
                        <button
                          key={sym.id}
                          className={`sym-menu-item ${activeTool === 'symbolStamp' && activeSymbol === sym.id ? 'active' : ''}`}
                          onClick={() => {
                            if (activeTool === 'symbolStamp' && activeSymbol === sym.id) {
                              setActiveTool('pan'); setActiveSymbol(null)
                            } else {
                              setActiveTool('symbolStamp'); setActiveSymbol(sym.id)
                              canvasRef.current?.cancelTool()
                            }
                            symbolDropdown.close()
                          }}
                          title={`Place ${sym.name}  —  click on drawing to stamp`}>
                          {sym.id === 'it' && (
                            <IconSymbolIT />
                          )}
                          {sym.id === 'main_entrance' && (
                            <IconSymbolEntrance />
                          )}
                          {sym.id === 'kitchen' && (
                            <IconSymbolKitchen />
                          )}
                          <span>{sym.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Snap toggle */}
                <button
                  className={`tool-btn ${snapEnabled ? 'active' : ''}`}
                  onClick={() => setSnapEnabled(s => !s)}
                  title={snapEnabled ? 'Snap ON  —  click to disable' : 'Snap OFF  —  click to enable'}>
                  <IconSnap />
                </button>

                {/* Active layer selector */}
                {(activeTool === 'draw' || activeTool === 'shape') && (
                  <select className="layer-select" value={activeLayer}
                    onChange={e => setActiveLayer(e.target.value)} title="Drawing layer">
                    {DRAW_LAYER_NAMES.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                )}
              </div>

              <div className="tool-divider" />

              {/* Undo / Redo */}
              <div className="tool-group">
                <button className={`tool-btn ${hl('undo')}`} onClick={undo} disabled={!histRef.current.length} title="Undo (Ctrl+Z)">
                  <IconUndo />
                </button>
                <button className="tool-btn" onClick={redo} disabled={!futRef.current.length} title="Redo (Ctrl+Shift+Z)">
                  <IconRedo />
                </button>
              </div>

              <div className="tool-divider" />

              {/* Zoom */}
              <div className="tool-group zoom-group">
                <button className={`tool-btn ${hl('zoom-extents')}`} onClick={() => canvasRef.current?.fitToScreen()} title="Zoom Extents  —  fit drawing to screen">
                  <IconZoomExtents />
                </button>
                <button className="tool-btn" onClick={() => canvasRef.current?.zoomBy(0.83)} title="Zoom out">
                  <IconMinus />
                </button>
                <button className="zoom-label" onClick={() => canvasRef.current?.fitToScreen()}>
                  {Math.round(zoom * 100) || 100}%
                </button>
                <button className="tool-btn" onClick={() => canvasRef.current?.zoomBy(1.2)} title="Zoom in">
                  <PlusIcon />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ChatPanel
        onHighlight={handleChatHighlight}
        appState={{
          hasUpload:    !!uploadData,
          hasClassify:  !!classifyData,
          approvedCount: approvedLayers.size,
          allApproved:  QBIQ_LAYER_NAMES.every(cls => approvedLayers.has(cls)),
        }}
      />

      {showPreExport && revalidateData && (
        <PreExportPopup
          data={revalidateData}
          classifyWarnings={classifyWarnings}
          onCancel={() => setShowPreExport(false)}
          onExport={() => { setShowPreExport(false); doExport() }}
        />
      )}

      {showWarningExport && (
        <div className="scale-dialog-overlay" onClick={() => setShowWarningExport(false)}>
          <div className="warn-dialog" onClick={e => e.stopPropagation()}>
            <div className="warn-dialog-icon">
              <IconWarningTriangle />
            </div>
            <h3 className="warn-dialog-title">Review before exporting</h3>
            <p className="warn-dialog-sub">The following issues were found in the drawing:</p>
            <div className="warn-dialog-list">
              {classifyWarnings.map((w, i) => (
                <div key={i} className="warn-dialog-item">
                  <IconAlertCircle style={{flexShrink:0,marginTop:1}} />
                  <span>{w}</span>
                </div>
              ))}
            </div>
            <div className="warn-dialog-actions">
              <button className="warn-btn-cancel" onClick={() => setShowWarningExport(false)}>Cancel</button>
              <button className="warn-btn-export" onClick={doExport}>Export Anyway</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
